import {
  canSnoozeThread,
  PROJECT_STUDY_NEW_THREAD_REASON,
  projectAwaitsStudy,
  resolveThreadCurrentPullRequestLink,
  threadManagementSection,
  threadWokeAt,
  threadWorkingStartedAt,
} from "@afar-research/afar";
import { useRightPanelStore } from "../rightPanelStore";
import { useProjectStudyDialogStore } from "../projectStudyDialogStore";
import { threadSnoozePresets } from "../threadSnooze.logic";
import { useThreadManagement } from "../hooks/useThreadManagement";
import { requestCustomSnooze } from "./CustomSnoozeDialog";
import { SidebarThreadUndoNotice } from "./sidebar/SidebarThreadUndoNotice";
import { undoLatestThreadAction } from "../hooks/showThreadUndoNotice";
import { isEditableFocused } from "../lib/editableFocus";
import { SidebarThreadSections } from "./SidebarThreadSections";
import type { SidebarDropVerb } from "./Sidebar.logic";
import { Schema } from "effect";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { ThreadTimeLabel } from "./ThreadTimeLabel";
import { SidebarThreadDetails } from "./SidebarThreadDetails";
import { MiddleTruncate } from "./ui/middle-truncate";
import { ProjectIcon } from "./ProjectIcon";
import {
  AlarmClockIcon,
  AlarmClockOffIcon,
  CheckIcon,
  ClockIcon,
  PinIcon,
  Undo2Icon,
} from "lucide-react";
import { Popover, PopoverTrigger, PopoverPopup } from "./ui/popover";
import { ProviderInstanceIcon } from "./chat/ProviderInstanceIcon";
import { getProjectWorkspacePreference, useProjectIcon } from "../projectPresentation";
import { SidebarProjectPicker } from "./SidebarProjectPicker";
import { SidebarProjectsGhost } from "./sidebar/SidebarProjectsGhost";
import { SidebarThreadHeader } from "./sidebar/SidebarThreadHeader";
import {
  CalendarClockIcon,
  ChartNoAxesColumnIcon,
  RefreshCwIcon,
  ChevronRightIcon,
  CloudIcon,
  Globe2Icon,
  GitPullRequestIcon,
  MessageSquareTextIcon,
  SettingsIcon,
  ListChecksIcon,
  SquarePenIcon,
  TerminalIcon,
} from "lucide-react";
import {
  ThreadPullRequestBadgeControl,
  linkedThreadPullRequestStatus,
  prStatusIndicator,
  resolveThreadPr,
  resolveThreadPullRequestBadge,
  terminalStatusFromRunningIds,
  ThreadStatusLabel,
  ThreadWorktreeIndicator,
} from "./ThreadStatusIndicators";
import { ProjectFavicon } from "./ProjectFavicon";
import { useAtomValue } from "@effect/atom-react";
import { autoAnimate } from "@formkit/auto-animate";
import React, { useCallback, useEffect, memo, useMemo, useRef, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import {
  type DragCancelEvent,
  type CollisionDetection,
  PointerSensor,
  type DragStartEvent,
  closestCorners,
  pointerWithin,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import type { useSortable } from "@dnd-kit/sortable";
import {
  type ContextMenuItem,
  DEFAULT_SERVER_SETTINGS,
  ProjectId,
  type ScopedThreadRef,
  type ResolvedKeybindingsConfig,
  type SidebarProjectGroupingMode,
  ThreadId,
} from "@afar-research/contracts";
import { commandFailureMessage } from "~/rpc/transportError";
import {
  parseScopedThreadKey,
  scopedProjectKey,
  scopedThreadKey,
  scopeProjectRef,
  scopeThreadRef,
} from "@afar-research/client-runtime/environment";
import { safeErrorLogAttributes } from "@afar-research/client-runtime/errors";
import {
  isAtomCommandInterrupted,
  settlePromise,
  squashAtomCommandFailure,
} from "@afar-research/client-runtime/state/runtime";
import {
  THREAD_DISCOVERY_RENDER_LIMIT,
  type ThreadDiscoveryResult,
} from "@afar-research/client-runtime/state/thread-discovery";
import { Link, useLocation, useNavigate, useParams, useRouter } from "@tanstack/react-router";
import {
  type SidebarProjectSortOrder,
  type SidebarThreadSortOrder,
} from "@afar-research/contracts/settings";
import { isElectron } from "../env";
import { APP_BRAND_BADGE } from "../branding";
import { useOpenPrLink } from "../lib/openPullRequestLink";
import { isTerminalFocused } from "../lib/terminalFocus";
import { resolveManagedWorktreeName } from "../lib/worktreeInventoryState";
import { cn, isMacPlatform } from "../lib/utils";
import {
  readThreadShell,
  useProject,
  useProjects,
  useServerConfigs,
  useThreadShells,
  useThreadShellsForProjectRefs,
} from "../state/entities";
import { selectThreadTerminalUiState, useTerminalUiStateStore } from "../terminalUiStateStore";
import { useThreadRunningTerminalIds } from "../state/terminalSessions";
import { useThreadDiscoveredPorts } from "../portDiscoveryState";
import { openDiscoveredPort } from "./preview/openDiscoveredPort";
import { useAtomCommand } from "../state/use-atom-command";
import { previewEnvironment } from "../state/preview";
import { resolveProjectExpanded, useUiStateStore } from "../uiStateStore";
import {
  resolveShortcutCommand,
  shortcutLabelForCommand,
  shouldShowThreadJumpHintsForModifiers,
  threadJumpCommandForIndex,
  threadJumpIndexFromCommand,
  threadTraversalDirectionFromCommand,
} from "../keybindings";
import { isModelPickerOpen } from "../modelPickerVisibility";
import { useShortcutModifierState } from "../shortcutModifierState";
import { readLocalApi } from "../localApi";
import { canSendFeedback, openFeedbackDialog } from "./settings/FeedbackDialog";
import { useComposerDraftStore } from "../composerDraftStore";
import { useNewThreadHandler } from "../hooks/useHandleNewThread";
import { useThreadDiscovery, useThreadDiscoveryPins } from "../threadDiscoveryContext";
import {
  hasProposedPlanHandoffForThread,
  useProposedPlanHandoffStore,
} from "../proposedPlanHandoffStore";

import { useThreadActions } from "../hooks/useThreadActions";
import { projectEnvironment } from "../state/projects";
import { useEnvironmentQuery } from "../state/query";
import { threadEnvironment, useEnvironmentThread } from "../state/threads";
import { vcsEnvironment } from "../state/vcs";
import { worktreeEnvironment } from "../state/worktrees";
import { useEnvironment, useEnvironments, usePrimaryEnvironmentId } from "../state/environments";
import { useWorkspaceShellEntry } from "../state/workspaceShellEntry";
import {
  resolveSidebarProjectScope,
  resolveVisibleSidebarProjectScopeKey,
  scopeAllowsThread,
} from "./sidebarProjectScope.logic";
import {
  buildThreadRouteParams,
  resolveThreadRouteRef,
  resolveThreadRouteTarget,
} from "../threadRoutes";
import { stackedThreadToast, toastManager } from "./ui/toast";
import { formatRelativeTimeLabel } from "../timestampFormat";
import { SettingsSidebarNav } from "./settings/SettingsSidebarNav";
import { Button } from "./ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPanel,
  DialogPopup,
  DialogTitle,
} from "./ui/dialog";
import { Input } from "./ui/input";
import { Select, SelectItem, SelectPopup, SelectTrigger, SelectValue } from "./ui/select";
import { Tooltip, TooltipPopup, TooltipTrigger } from "./ui/tooltip";
import {
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  SidebarSeparator,
  SidebarTrigger,
  useSidebar,
} from "./ui/sidebar";
import { useThreadSelectionStore } from "../threadSelectionStore";
import { useOpenAddProjectCommandPalette } from "../commandPaletteContext";
import {
  areAllProjectEnvironmentsUnavailable,
  getSidebarProjectKeysWithThreads,
  getSidebarThreadPrewarmPlan,
  hasReadyProjectEnvironment,
  resolveAdjacentThreadId,
  isContextMenuPointerDown,
  isTrailingDoubleClick,
  resolveProjectStatusIndicator,
  resolveSidebarNewThreadProjectMember,
  searchSidebarThreads,
  resolveSidebarNewThreadSeedContext,
  resolveSidebarNewThreadEnvMode,
  resolveThreadRowClassName,
  resolveThreadStatusPill,
  sidebarFooterDestination,
  orderItemsByPreferredIds,
  shouldClearThreadSelectionOnMouseDown,
  supportsBoundedThreadHistoryPrewarm,
  sortProjectsForSidebar,
  useThreadJumpHintVisibility,
} from "./Sidebar.logic";
import { sortThreads } from "../lib/threadSort";
import { useCopyToClipboard } from "~/hooks/useCopyToClipboard";
import { useIsMobile } from "~/hooks/useMediaQuery";
import { useClientSettings, useUpdateClientSettings } from "~/hooks/useSettings";
import { primaryServerKeybindingsAtom } from "../state/server";
import {
  derivePhysicalProjectKey,
  deriveProjectGroupingOverrideKey,
  getProjectOrderKey,
  selectProjectGroupingSettings,
} from "../logicalProject";
import type { SidebarThreadSummary } from "../types";
import {
  buildPhysicalToLogicalProjectKeyMap,
  buildSidebarProjectSnapshots,
  connectedSidebarProjects,
  PROJECT_GROUPING_MODE_LABELS,
  type SidebarProjectGroupMember,
  type SidebarProjectSnapshot,
} from "../sidebarProjectGrouping";
const SIDEBAR_LIST_ANIMATION_OPTIONS = {
  duration: 180,
  easing: "ease-out",
} as const;
const EMPTY_THREAD_JUMP_LABELS = new Map<string, string>();

const SIDEBAR_ICON_ACTION_BUTTON_CLASS =
  "inline-flex h-6 min-w-6 cursor-pointer items-center justify-center rounded-md px-[calc(--spacing(1)-1px)] text-muted-foreground/60 hover:text-foreground focus-visible:outline-hidden focus-visible:ring-1 focus-visible:ring-ring";

function SidebarThreadDetailPrewarmer({ threadRef }: { readonly threadRef: ScopedThreadRef }) {
  useEnvironmentThread(threadRef.environmentId, threadRef.threadId);
  return null;
}

function formatProjectMemberActionLabel(
  member: SidebarProjectGroupMember,
  groupedProjectCount: number,
): string {
  if (groupedProjectCount <= 1) {
    return member.title;
  }

  return member.environmentLabel
    ? `${member.environmentLabel} — ${member.workspaceRoot}`
    : member.workspaceRoot;
}

function projectExpansionPreferenceKeys(project: SidebarProjectSnapshot): string[] {
  return [project.projectKey, ...project.memberProjects.map((member) => member.physicalProjectKey)];
}

function projectGroupingModeDescription(mode: SidebarProjectGroupingMode): string {
  switch (mode) {
    case "repository":
      return "Projects from the same repository share one sidebar row.";
    case "repository_path":
      return "Projects group only when both the repository and repo-relative path match.";
    case "separate":
      return "Every project path gets its own sidebar row.";
  }
}

function buildThreadJumpLabelMap(input: {
  keybindings: ResolvedKeybindingsConfig;
  platform: string;
  terminalOpen: boolean;
  threadJumpCommandByKey: ReadonlyMap<
    string,
    NonNullable<ReturnType<typeof threadJumpCommandForIndex>>
  >;
}): ReadonlyMap<string, string> {
  if (input.threadJumpCommandByKey.size === 0) {
    return EMPTY_THREAD_JUMP_LABELS;
  }

  const shortcutLabelOptions = {
    platform: input.platform,
    context: {
      terminalFocus: false,
      terminalOpen: input.terminalOpen,
    },
  } as const;
  const mapping = new Map<string, string>();
  for (const [threadKey, command] of input.threadJumpCommandByKey) {
    const label = shortcutLabelForCommand(input.keybindings, command, shortcutLabelOptions);
    if (label) {
      mapping.set(threadKey, label);
    }
  }
  return mapping.size > 0 ? mapping : EMPTY_THREAD_JUMP_LABELS;
}

/** One row of the snooze menu: a preset with its wake time, or Custom…. */
const snoozePresetClass =
  "flex w-full cursor-pointer items-center gap-2 rounded-md px-2 py-1.5 text-left text-xs text-foreground/90 hover:bg-accent hover:text-foreground";

interface SidebarThreadRowProps {
  manageThread: ReturnType<typeof useThreadManagement>;
  pinned: boolean;
  togglePin: (ref: ScopedThreadRef) => void;
  thread: SidebarThreadSummary;
  projectCwd: string | null;
  orderedProjectThreadKeys: readonly string[];
  isActive: boolean;
  jumpLabel: string | null;
  /** What releasing here would do, shown on the row while it is lifted. */
  dropVerb?: SidebarDropVerb | null;
  renamingThreadKey: string | null;
  renamingTitle: string;
  setRenamingTitle: (title: string) => void;
  startThreadRename: (threadKey: string, title: string) => void;
  renamingInputRef: React.RefObject<HTMLInputElement | null>;
  renamingCommittedRef: React.RefObject<boolean>;
  handleThreadClick: (
    event: React.MouseEvent,
    threadRef: ScopedThreadRef,
    orderedProjectThreadKeys: readonly string[],
  ) => void;
  navigateToThread: (threadRef: ScopedThreadRef) => void;
  handleMultiSelectContextMenu: (position: { x: number; y: number }) => Promise<void>;
  handleThreadContextMenu: (
    threadRef: ScopedThreadRef,
    position: { x: number; y: number },
  ) => Promise<void>;
  clearSelection: () => void;
  commitRename: (
    threadRef: ScopedThreadRef,
    newTitle: string,
    originalTitle: string,
  ) => Promise<void>;
  cancelRename: () => void;
  openPrLink: (
    event: React.MouseEvent<HTMLElement>,
    prUrl: string,
    threadRef?: ScopedThreadRef,
  ) => void;
}

export const SidebarThreadRow = memo(function SidebarThreadRow(props: SidebarThreadRowProps) {
  const {
    manageThread,
    pinned,
    togglePin,
    orderedProjectThreadKeys,
    isActive,
    jumpLabel,
    dropVerb = null,
    renamingThreadKey,
    renamingTitle,
    setRenamingTitle,
    startThreadRename,
    renamingInputRef,
    renamingCommittedRef,
    handleThreadClick,
    navigateToThread,
    handleMultiSelectContextMenu,
    handleThreadContextMenu,
    clearSelection,
    commitRename,
    cancelRename,
    openPrLink,
    thread,
  } = props;
  const provider = useServerConfigs()
    .get(thread.environmentId)
    ?.providers.find((provider) => provider.instanceId === thread.modelSelection.instanceId);
  const threadRef = scopeThreadRef(thread.environmentId, thread.id);
  const threadKey = scopedThreadKey(threadRef);
  const suppressPlanReady = useProposedPlanHandoffStore((state) =>
    hasProposedPlanHandoffForThread(state, threadRef),
  );
  const lastVisitedAt = useUiStateStore((state) => state.threadLastVisitedAtById[threadKey]);
  const markThreadVisited = useUiStateStore((state) => state.markThreadVisited);
  const isSelected = useThreadSelectionStore((state) => state.selectedThreadKeys.has(threadKey));
  const runningTerminalIds = useThreadRunningTerminalIds({
    environmentId: thread.environmentId,
    threadId: thread.id,
  });
  const isMobile = useIsMobile();
  const discoveredPorts = useThreadDiscoveredPorts({
    environmentId: thread.environmentId,
    threadId: thread.id,
  });
  const openPreview = useAtomCommand(previewEnvironment.open, {
    reportFailure: false,
  });
  const environment = useEnvironment(thread.environmentId);
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  const isRemoteThread =
    primaryEnvironmentId !== null && thread.environmentId !== primaryEnvironmentId;
  const remoteEnvLabel = environment?.label ?? null;
  const threadEnvironmentLabel = isRemoteThread ? (remoteEnvLabel ?? "Remote") : null;
  // For grouped projects, the thread may belong to a different environment
  // than the representative project.  Look up the thread's own project cwd
  // so git status (and thus PR detection) queries the correct path.
  const threadProject = useProject(
    useMemo(
      () => scopeProjectRef(thread.environmentId, thread.projectId),
      [thread.environmentId, thread.projectId],
    ),
  );
  const projectIcon = useProjectIcon(scopeProjectRef(thread.environmentId, thread.projectId));
  const threadProjectCwd = threadProject?.workspaceRoot ?? null;
  const gitCwd = thread.worktreePath ?? threadProjectCwd ?? props.projectCwd;
  const gitStatus = useEnvironmentQuery(
    thread.branch != null && gitCwd !== null
      ? vcsEnvironment.status({
          environmentId: thread.environmentId,
          input: { cwd: gitCwd },
        })
      : null,
  );
  const managedWorktrees = useEnvironmentQuery(
    thread.worktreePath === null
      ? null
      : worktreeEnvironment.managed({ environmentId: thread.environmentId, input: {} }),
  );
  const worktreeName = resolveManagedWorktreeName({
    worktrees: managedWorktrees.data?.worktrees ?? [],
    threadId: thread.id,
    worktreePath: thread.worktreePath,
  });
  const handleOpenDiscoveredPort = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      const port = discoveredPorts[0];
      if (!port) return;
      event.preventDefault();
      event.stopPropagation();
      navigateToThread(threadRef);
      void (async () => {
        const result = await openDiscoveredPort({ threadRef, port, openPreview });
        if (result._tag === "Success" || isAtomCommandInterrupted(result)) {
          return;
        }
        const error = squashAtomCommandFailure(result);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Unable to open preview",
            description: commandFailureMessage(error, "The preview could not be opened."),
          }),
        );
      })();
    },
    [discoveredPorts, navigateToThread, openPreview, threadRef],
  );
  const threadStatus = resolveThreadStatusPill({
    suppressPlanReady,
    thread: {
      ...thread,
      lastVisitedAt,
    },
  });
  // A thread's own collection is durable state the Environment already holds, so it answers
  // first; the branch's pull request only speaks for a thread that has linked none.
  const currentLink = useMemo(
    () => resolveThreadCurrentPullRequestLink(thread.pullRequests ?? []),
    [thread.pullRequests],
  );
  const linkedStatus = useMemo(
    () => (currentLink === null ? null : linkedThreadPullRequestStatus(currentLink)),
    [currentLink],
  );
  // Once a thread links anything, the branch's own pull request stops speaking for it — the two
  // are often different pull requests, and a badge showing one's number over the other's state
  // would open a pull request this thread does not link.
  const pr =
    linkedStatus?.pr ??
    (currentLink === null ? resolveThreadPr(thread.branch, gitStatus.data) : null);
  const prStatus = prStatusIndicator(
    pr,
    linkedStatus?.sourceControlProvider ?? gitStatus.data?.sourceControlProvider,
  );
  const prBadge = useMemo(
    () => resolveThreadPullRequestBadge(thread.pullRequests),
    [thread.pullRequests],
  );
  const terminalStatus = terminalStatusFromRunningIds(runningTerminalIds);

  const handleRowClick = useCallback(
    (event: React.MouseEvent) => {
      handleThreadClick(event, threadRef, orderedProjectThreadKeys);
    },
    [handleThreadClick, orderedProjectThreadKeys, threadRef],
  );
  const handleRowDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      // Already renaming this row: a double-click on the row chrome (outside the
      // input) must not restart and discard the in-progress edit.
      if (renamingThreadKey === threadKey) return;
      // On mobile the first tap navigates and closes the sidebar sheet, so the
      // inline rename can't be shown. Renaming there stays on the context menu.
      if (isMobile) return;
      // cmd/ctrl/shift double-clicks are multi-select intent, not rename.
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      // Ignore double-clicks bubbling from nested controls (PR status, port,
      // archive buttons) — only the row body should enter inline rename.
      if ((event.target as HTMLElement).closest("button, a")) return;
      event.preventDefault();
      startThreadRename(threadKey, thread.title);
    },
    [isMobile, renamingThreadKey, startThreadRename, threadKey, thread.title],
  );
  const handleRowKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.target !== event.currentTarget) return;
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      navigateToThread(threadRef);
    },
    [navigateToThread, threadRef],
  );
  const handleRowContextMenu = useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      const hasSelection = useThreadSelectionStore.getState().hasSelection();
      if (hasSelection && isSelected) {
        void (async () => {
          const result = await settlePromise(() =>
            handleMultiSelectContextMenu({
              x: event.clientX,
              y: event.clientY,
            }),
          );
          if (result._tag === "Failure") {
            const error = squashAtomCommandFailure(result);
            toastManager.add(
              stackedThreadToast({
                type: "error",
                title: "Thread action failed",
                description: commandFailureMessage(error, "An error occurred."),
              }),
            );
          }
        })();
        return;
      }

      if (hasSelection) {
        clearSelection();
      }
      void (async () => {
        const result = await settlePromise(() =>
          handleThreadContextMenu(threadRef, {
            x: event.clientX,
            y: event.clientY,
          }),
        );
        if (result._tag === "Failure") {
          const error = squashAtomCommandFailure(result);
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Thread action failed",
              description: commandFailureMessage(error, "An error occurred."),
            }),
          );
        }
      })();
    },
    [clearSelection, handleMultiSelectContextMenu, handleThreadContextMenu, isSelected, threadRef],
  );
  const handlePrClick = useCallback(
    (event: React.MouseEvent<HTMLElement>) => {
      // A link the Environment has not read yet still has an address, and the badge is already
      // showing its number: the click has to reach it rather than do nothing.
      const url = currentLink?.url ?? prStatus?.url;
      if (url === undefined) return;
      openPrLink(event, url, threadRef);
    },
    [currentLink, openPrLink, prStatus, threadRef],
  );
  // No single pull request stands for a stack, so its badge opens the collection instead.
  const handlePrStackClick = useCallback(() => {
    useRightPanelStore.getState().open(threadRef, "pull-requests");
    // The panel is scoped to this row's thread, so the conversation beside it has to be that
    // thread's too — otherwise the collection describes work the chat is not showing.
    if (!isActive) navigateToThread(threadRef);
  }, [isActive, navigateToThread, threadRef]);
  const handleRenameInputRef = useCallback(
    (element: HTMLInputElement | null) => {
      if (element && renamingInputRef.current !== element) {
        renamingInputRef.current = element;
        element.focus();
        element.select();
      }
    },
    [renamingInputRef],
  );
  const handleRenameInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setRenamingTitle(event.target.value);
    },
    [setRenamingTitle],
  );
  const handleRenameInputKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.key === "Enter") {
        event.preventDefault();
        renamingCommittedRef.current = true;
        void commitRename(threadRef, renamingTitle, thread.title);
      } else if (event.key === "Escape") {
        event.preventDefault();
        renamingCommittedRef.current = true;
        cancelRename();
      }
    },
    [cancelRename, commitRename, renamingCommittedRef, renamingTitle, thread.title, threadRef],
  );
  const handleRenameInputBlur = useCallback(() => {
    if (!renamingCommittedRef.current) {
      void commitRename(threadRef, renamingTitle, thread.title);
    }
  }, [commitRename, renamingCommittedRef, renamingTitle, thread.title, threadRef]);
  // Keep clicks/double-clicks inside the rename input from bubbling to the row.
  // Without stopping `dblclick`, double-clicking to select a word would re-fire
  // the row's rename handler and reset the in-progress edit back to the title.
  const handleRenameInputClick = useCallback((event: React.MouseEvent<HTMLInputElement>) => {
    event.stopPropagation();
  }, []);
  const rowButtonRender = useMemo(() => <div role="button" tabIndex={0} />, []);

  const section = threadManagementSection(thread, Date.now());
  // Settled and snoozed are the ONLY things that collapse a row: every other
  // thread is a full card. Density comes from users (or the auto rules) actually
  // parking work, not from the sidebar second-guessing what still matters.
  // Pinning outranks both: a pinned row was put there on purpose, so it keeps
  // its card even once it settles, the way it does in the Pinned block.
  const slim = !pinned && section !== "active";
  // Kept separate from the variant: the reverse action follows what the thread
  // actually IS, so a pinned settled row still offers Un-settle from its card.
  const variantAction =
    section === "snoozed" ? "unsnooze" : section === "settled" ? "unsettle" : "settle";
  const [snoozeOpenRaw, setSnoozeOpen] = useState(false);
  const canSnooze = section === "active" && canSnoozeThread(thread);
  const snoozeOpen = snoozeOpenRaw && canSnooze;
  useEffect(() => {
    if (!canSnooze) setSnoozeOpen(false);
  }, [canSnooze]);
  const presets = useMemo(() => (snoozeOpen ? threadSnoozePresets(new Date()) : []), [snoozeOpen]);
  // A wake the user has not seen yet. Visiting the thread clears it, the same
  // way visiting clears unread, so an opened thread never keeps shouting.
  const wokeAt = threadWokeAt(thread, Date.now());
  const isWoke =
    wokeAt !== null && (lastVisitedAt == null || Date.parse(lastVisitedAt) < Date.parse(wokeAt));
  const handleAcknowledgeWokeClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      if (wokeAt) markThreadVisited(threadKey, wokeAt);
    },
    [markThreadVisited, threadKey, wokeAt],
  );
  const isRegeneratingTitle = thread.titleRegeneration != null;
  const actionClass =
    "inline-flex h-full cursor-pointer items-center gap-1 rounded-md bg-transparent px-1.5 text-xs text-muted-foreground hover:text-foreground";
  const favicon = <ProjectIcon icon={projectIcon} className="size-4" />;
  const title =
    renamingThreadKey === threadKey ? (
      <input
        ref={handleRenameInputRef}
        className="min-w-0 flex-1 truncate rounded border border-ring bg-transparent px-0.5 text-xs outline-none"
        value={renamingTitle}
        onChange={handleRenameInputChange}
        onKeyDown={handleRenameInputKeyDown}
        onBlur={handleRenameInputBlur}
        onClick={handleRenameInputClick}
        onDoubleClick={handleRenameInputClick}
      />
    ) : (
      <span
        className={cn(
          "min-w-0 flex-1 truncate text-sm font-normal",
          // The title on screen is the one being replaced; dimming it says so
          // without moving the row or repainting anything.
          isRegeneratingTitle && "opacity-[0.55]",
        )}
        data-testid={`thread-title-${thread.id}`}
        aria-busy={isRegeneratingTitle || undefined}
      >
        {thread.title}
        {isRegeneratingTitle ? (
          <span role="status" className="sr-only">
            Regenerating title
          </span>
        ) : null}
      </span>
    );
  const status =
    section === "snoozed" && thread.management?.snoozedUntil ? (
      <span className="text-blue-600 dark:text-blue-400">
        <ThreadTimeLabel at={thread.management.snoozedUntil} mode="snoozed" />
      </span>
    ) : section === "settled" && thread.management?.settledAt ? (
      <ThreadTimeLabel at={thread.management.settledAt} mode="settled" />
    ) : isWoke ? (
      // Woke is itself an action, so it is a button rather than a label: a
      // woken thread returns to its old place in the list, and this is the only
      // thing saying it came back.
      <Tooltip>
        <TooltipTrigger
          render={
            <button
              type="button"
              aria-label="Dismiss Woke notification"
              onClick={handleAcknowledgeWokeClick}
              className="inline-flex cursor-pointer items-center gap-1 rounded-sm font-medium text-amber-700 outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-300"
            />
          }
        >
          <AlarmClockIcon aria-hidden className="size-4 shrink-0" />
          <span role="status">Woke</span>
        </TooltipTrigger>
        <TooltipPopup side="top">Dismiss Woke notification</TooltipPopup>
      </Tooltip>
    ) : threadStatus ? (
      <span className="flex items-center gap-1">
        <ThreadStatusLabel status={threadStatus} />
        {threadStatus.label === "Working" && threadWorkingStartedAt(thread) ? (
          <span aria-hidden>
            <ThreadTimeLabel at={threadWorkingStartedAt(thread)!} mode="working" />
          </span>
        ) : null}
      </span>
    ) : (
      formatRelativeTimeLabel(thread.latestUserMessageAt ?? thread.updatedAt)
        .replace(/ ago$/, "")
        .replace("just now", "now")
    );
  const pinIndicator = pinned ? (
    <button
      type="button"
      aria-label="Unpin thread"
      className="inline-flex cursor-pointer items-center rounded-sm text-muted-foreground/65 hover:text-foreground"
      onClick={(event) => {
        event.stopPropagation();
        togglePin(threadRef);
      }}
    >
      <PinIcon className="size-3 shrink-0" />
    </button>
  ) : null;
  const actions = (
    <span
      className={cn(
        "pointer-events-none absolute inset-y-0 right-0 flex items-stretch opacity-0 has-[:focus-visible]:pointer-events-auto has-[:focus-visible]:static has-[:focus-visible]:opacity-100 group-hover/sidebar-row:pointer-events-auto group-hover/sidebar-row:static group-hover/sidebar-row:opacity-100",
        (slim || snoozeOpen) && "pointer-events-auto static opacity-100",
      )}
    >
      {!slim && !pinned && (
        <button
          type="button"
          aria-label="Pin thread"
          className={actionClass}
          onClick={(event) => {
            event.stopPropagation();
            togglePin(threadRef);
          }}
        >
          <PinIcon className="size-3" />
        </button>
      )}
      {canSnooze && (
        <Popover open={snoozeOpen} onOpenChange={setSnoozeOpen}>
          <PopoverTrigger
            render={
              <button
                type="button"
                aria-label="Snooze thread"
                className={actionClass}
                onClick={(event) => event.stopPropagation()}
                onDoubleClick={(event) => event.stopPropagation()}
              />
            }
          >
            <ClockIcon className="size-3" />
          </PopoverTrigger>
          <PopoverPopup side="bottom" align="end" className="w-56" viewportClassName="p-1">
            {presets.map((preset) => (
              <button
                key={preset.id}
                type="button"
                className={snoozePresetClass}
                onClick={(event) => {
                  event.stopPropagation();
                  setSnoozeOpen(false);
                  void manageThread(threadRef, "snooze", preset.until);
                }}
              >
                <span className="flex-1">{preset.label}</span>
                <span className="font-mono text-[10px] text-muted-foreground/60 tabular-nums">
                  {preset.whenLabel}
                </span>
              </button>
            ))}
            <div className="my-1 h-px bg-border" role="none" />
            <button
              type="button"
              className={snoozePresetClass}
              onClick={async (event) => {
                event.stopPropagation();
                setSnoozeOpen(false);
                const choice = await requestCustomSnooze();
                if (choice) void manageThread(threadRef, "snooze", choice.until);
              }}
            >
              <span className="flex-1">Custom…</span>
            </button>
          </PopoverPopup>
        </Popover>
      )}
      <button
        type="button"
        aria-label={
          variantAction === "unsnooze"
            ? "Unsnooze thread"
            : variantAction === "unsettle"
              ? "Un-settle thread"
              : "Settle thread"
        }
        className={actionClass}
        onClick={(event) => {
          event.stopPropagation();
          void manageThread(
            threadRef,
            variantAction === "unsnooze"
              ? "unsnooze"
              : variantAction === "unsettle"
                ? "restore"
                : "settle",
          );
        }}
      >
        {variantAction === "unsnooze" ? (
          <>
            <AlarmClockOffIcon className="size-3" />
            Unsnooze
          </>
        ) : variantAction === "unsettle" ? (
          <>
            <Undo2Icon className="size-3.5" />
            Un-settle
          </>
        ) : (
          <>
            <CheckIcon className="size-3.5" />
            Settle
          </>
        )}
      </button>
    </span>
  );
  const statusSlot = (
    <span className="relative ml-auto flex h-5 min-w-8 shrink-0 items-stretch justify-end text-xs">
      <span
        className={cn(
          "flex items-center self-center tabular-nums text-muted-foreground",
          // Read-only status yields to the hover actions. Woke is itself an
          // action, so it stays visible and clickable while they appear beside
          // it — hovering the row must not hide the only way to dismiss it.
          !slim &&
            !isWoke &&
            "group-hover/sidebar-row:absolute group-hover/sidebar-row:right-0 group-hover/sidebar-row:opacity-0",
          snoozeOpen && !isWoke && "absolute right-0 opacity-0",
        )}
      >
        {status}
      </span>
      {actions}
    </span>
  );
  return (
    <SidebarMenuSubItem
      className={cn(
        // The intrinsic size matches the row's content box so rows the browser
        // skips never shift the list as it scrolls past them: h-9 for slim,
        // h-[4.875rem] plus the py-0.5 padding for a card.
        "w-full [content-visibility:auto]",
        slim ? "[contain-intrinsic-size:auto_36px]" : "py-0.5 [contain-intrinsic-size:auto_78px]",
      )}
      data-thread-item
    >
      <Tooltip disabled={snoozeOpen}>
        <TooltipTrigger
          render={
            <SidebarMenuSubButton
              render={rowButtonRender}
              size="sm"
              isActive={isActive}
              data-testid={`thread-row-${thread.id}`}
              className={cn(
                resolveThreadRowClassName({ isActive, isSelected }),
                "group/sidebar-row relative isolate rounded-lg!",
                slim
                  ? "flex! h-9! items-center! gap-2.5! px-[var(--sidebar-row-content-inset)]!"
                  : "block! h-[4.875rem]! px-[var(--sidebar-row-content-inset)]! py-[var(--sidebar-content-inset)]!",
              )}
              onClick={handleRowClick}
              onDoubleClick={handleRowDoubleClick}
              onKeyDown={handleRowKeyDown}
              onContextMenu={handleRowContextMenu}
            />
          }
        >
          {slim ? (
            <>
              <span
                className={cn(
                  !isActive &&
                    "opacity-40 grayscale group-hover/sidebar-row:opacity-100 group-hover/sidebar-row:grayscale-0",
                )}
              >
                {favicon}
              </span>
              {title}
              {pinIndicator}
              {statusSlot}
            </>
          ) : (
            <>
              <div className="flex h-5 min-w-0 items-center gap-1.5">
                {favicon}
                <span className="min-w-0 flex-1 truncate text-xs text-muted-foreground">
                  {threadProject?.title ?? "Project"}
                </span>
                {threadProject !== null && projectAwaitsStudy(threadProject) ? (
                  <span className="shrink-0 text-[9px] font-medium text-muted-foreground">
                    Awaiting study
                  </span>
                ) : null}
                {pinIndicator}
                {statusSlot}
              </div>
              <div className="mt-1 flex min-w-0">{title}</div>
              <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground">
                <ThreadWorktreeIndicator thread={thread} worktreeName={worktreeName} />
                {thread.branch ? (
                  <span className="flex min-w-0 flex-1 text-muted-foreground/40">
                    <MiddleTruncate value={thread.branch} showTitle={false} />
                  </span>
                ) : (
                  <span className="flex-1" />
                )}
                {discoveredPorts.length > 0 && (
                  <button
                    type="button"
                    aria-label={`Open port ${discoveredPorts[0]?.port}`}
                    onClick={handleOpenDiscoveredPort}
                  >
                    <Globe2Icon className="size-3" />
                  </button>
                )}
                {terminalStatus && (
                  <TerminalIcon
                    aria-label={terminalStatus.label}
                    className={cn(
                      "size-3.5",
                      terminalStatus.colorClass,
                      terminalStatus.pulse && "animate-status-pulse",
                    )}
                  />
                )}
                {prBadge !== null || prStatus ? (
                  <ThreadPullRequestBadgeControl
                    badge={prBadge}
                    number={currentLink?.number ?? pr?.number}
                    url={currentLink?.url ?? pr?.url}
                    status={prStatus}
                    pullRequests={thread.pullRequests}
                    onOpenStack={handlePrStackClick}
                    onOpenPullRequest={handlePrClick}
                  />
                ) : null}
                {isRemoteThread && (
                  <CloudIcon aria-label={threadEnvironmentLabel ?? "Remote"} className="size-3" />
                )}
                {dropVerb ? (
                  <span className="rounded bg-primary/15 px-1 text-[9px] font-medium capitalize text-primary">
                    {dropVerb}
                  </span>
                ) : null}
                {jumpLabel && <span className="text-[9px]">{jumpLabel}</span>}
                {provider && (
                  <ProviderInstanceIcon
                    driverKind={provider.driver}
                    displayName={provider.displayName ?? provider.instanceId}
                    iconClassName="size-3.5 text-muted-foreground"
                  />
                )}
              </div>
            </>
          )}
        </TooltipTrigger>
        <TooltipPopup
          side="right"
          align="start"
          sideOffset={4}
          className="max-w-80 whitespace-normal text-left leading-tight"
        >
          <SidebarThreadDetails
            title={thread.title}
            project={threadProject?.title ?? "Project"}
            projectIcon={projectIcon}
            environment={environment?.label ?? String(thread.environmentId)}
            branch={thread.branch}
            model={thread.modelSelection.model}
          />
        </TooltipPopup>
      </Tooltip>
    </SidebarMenuSubItem>
  );
});

interface SidebarProjectItemProps {
  scopeProjectSnapshots?: readonly SidebarProjectSnapshot[];
  project: SidebarProjectSnapshot;
  activeRouteThreadKey: string | null;
  newThreadShortcutLabel: string | null;
  handleNewThread: ReturnType<typeof useNewThreadHandler>;
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  threadJumpLabelByKey: ReadonlyMap<string, string>;
  dragInProgressRef: React.RefObject<boolean>;
  suppressProjectClickAfterDragRef: React.RefObject<boolean>;
  suppressProjectClickForContextMenuRef: React.RefObject<boolean>;
  isManualProjectSorting: boolean;
  dragHandleProps: SortableProjectHandleProps | null;
}

const SidebarProjectItem = memo(function SidebarProjectItem(props: SidebarProjectItemProps) {
  const {
    project,
    activeRouteThreadKey,
    newThreadShortcutLabel,
    handleNewThread,
    archiveThread,
    deleteThread,
    threadJumpLabelByKey,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    suppressProjectClickForContextMenuRef,
    isManualProjectSorting,
    dragHandleProps,
  } = props;
  const threadMembers = useMemo(
    () =>
      props.scopeProjectSnapshots?.flatMap((entry) => entry.memberProjects) ??
      project.memberProjects,
    [props.scopeProjectSnapshots, project.memberProjects],
  );
  const threadMemberRefs = useMemo(
    () =>
      props.scopeProjectSnapshots?.flatMap((entry) => entry.memberProjectRefs) ??
      project.memberProjectRefs,
    [props.scopeProjectSnapshots, project.memberProjectRefs],
  );
  const threadSortOrder = useClientSettings<SidebarThreadSortOrder>(
    (settings) => settings.sidebarThreadSortOrder,
  );
  const appSettingsConfirmThreadDelete = useClientSettings<boolean>(
    (settings) => settings.confirmThreadDelete,
  );
  const appSettingsConfirmThreadArchive = useClientSettings<boolean>(
    (settings) => settings.confirmThreadArchive,
  );
  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const serverConfigs = useServerConfigs();
  const unavailableEnvironmentIds = useMemo(
    () =>
      new Set(
        threadMembers.flatMap((member) => {
          const ready = serverConfigs.has(member.environmentId);
          return ready ? [] : [member.environmentId];
        }),
      ),
    [threadMembers, serverConfigs],
  );
  const allProjectEnvironmentsUnavailable = areAllProjectEnvironmentsUnavailable(
    threadMembers,
    unavailableEnvironmentIds,
  );
  const scheduleProjectMember = threadMembers[0];
  const deleteProject = useAtomCommand(projectEnvironment.delete, {
    reportFailure: false,
  });
  const updateProject = useAtomCommand(projectEnvironment.update, {
    reportFailure: false,
  });
  const updateThreadMetadata = useAtomCommand(threadEnvironment.updateMetadata, {
    reportFailure: false,
  });
  const updateSettings = useUpdateClientSettings();
  const pinnedThreadRefs = useClientSettings((settings) => settings.pinnedThreadRefs);
  const { pinThread, unpinThread } = useThreadDiscoveryPins();
  const pinnedKeys = useMemo(
    () => new Set(pinnedThreadRefs.map(scopedThreadKey)),
    [pinnedThreadRefs],
  );
  const confirmThreadUnpin = useClientSettings((settings) => settings.confirmThreadUnpin);
  // Unpinning throws away an arrangement the user made by hand, and the pin
  // marker sits right where a stray click lands, so the confirmation is worth
  // offering even though pinning back is cheap.
  const togglePin = useCallback(
    (ref: ScopedThreadRef) => {
      if (!pinnedKeys.has(scopedThreadKey(ref))) {
        pinThread(ref);
        return;
      }
      if (!confirmThreadUnpin) {
        unpinThread(ref);
        return;
      }
      const localApi = readLocalApi();
      if (!localApi) {
        unpinThread(ref);
        return;
      }
      void localApi.dialogs.confirm("Unpin this thread?").then((confirmed) => {
        if (confirmed) unpinThread(ref);
      });
    },
    [confirmThreadUnpin, pinnedKeys, pinThread, unpinThread],
  );
  const router = useRouter();

  const { isMobile, setOpenMobile } = useSidebar();
  const markThreadUnread = useUiStateStore((state) => state.markThreadUnread);
  const setProjectExpanded = useUiStateStore((state) => state.setProjectExpanded);
  const toggleThreadSelection = useThreadSelectionStore((state) => state.toggleThread);
  const rangeSelectTo = useThreadSelectionStore((state) => state.rangeSelectTo);
  const clearSelection = useThreadSelectionStore((state) => state.clearSelection);
  const removeFromSelection = useThreadSelectionStore((state) => state.removeFromSelection);
  const setSelectionAnchor = useThreadSelectionStore((state) => state.setAnchor);
  const proposedPlanHandoffsBySourceThreadKey = useProposedPlanHandoffStore(
    (state) => state.bySourceThreadKey,
  );
  const { copyToClipboard: copyThreadIdToClipboard } = useCopyToClipboard<{
    threadId: ThreadId;
  }>({
    onCopy: (ctx) => {
      toastManager.add({
        type: "success",
        title: "Thread ID copied",
        description: ctx.threadId,
      });
    },
    onError: (error) => {
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Failed to copy thread ID",
          description: commandFailureMessage(error, "An error occurred."),
        }),
      );
    },
  });
  const { copyToClipboard: copyPathToClipboard } = useCopyToClipboard<{
    path: string;
  }>({
    onCopy: (ctx) => {
      toastManager.add({
        type: "success",
        title: "Path copied",
        description: ctx.path,
      });
    },
    onError: (error) => {
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Failed to copy path",
          description: commandFailureMessage(error, "An error occurred."),
        }),
      );
    },
  });
  const openPrLink = useOpenPrLink();
  const sidebarThreads = useThreadShellsForProjectRefs(threadMemberRefs);
  const sidebarThreadByKey = useMemo(
    () =>
      new Map(
        sidebarThreads.map(
          (thread) =>
            [scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)), thread] as const,
        ),
      ),
    [sidebarThreads],
  );
  // Keep a ref so callbacks can read the latest map without appearing in
  // dependency arrays (avoids invalidating every thread-row memo on each
  // thread-list change).
  const sidebarThreadByKeyRef = useRef(sidebarThreadByKey);
  sidebarThreadByKeyRef.current = sidebarThreadByKey;
  const projectThreads = sidebarThreads;
  const projectPreferenceKeys = useMemo(() => projectExpansionPreferenceKeys(project), [project]);
  const savedProjectExpanded = useUiStateStore((state) =>
    resolveProjectExpanded(state.projectExpandedById, projectPreferenceKeys),
  );
  const projectExpanded = props.scopeProjectSnapshots !== undefined || savedProjectExpanded;
  const threadLastVisitedAts = useUiStateStore(
    useShallow((state) =>
      projectThreads.map(
        (thread) =>
          state.threadLastVisitedAtById[
            scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id))
          ] ?? null,
      ),
    ),
  );
  const [renamingThreadKey, setRenamingThreadKey] = useState<string | null>(null);
  const [renamingTitle, setRenamingTitle] = useState("");
  const [projectRenameTarget, setProjectRenameTarget] = useState<SidebarProjectGroupMember | null>(
    null,
  );
  const [projectRenameTitle, setProjectRenameTitle] = useState("");
  const [projectGroupingTarget, setProjectGroupingTarget] =
    useState<SidebarProjectGroupMember | null>(null);
  const [projectGroupingSelection, setProjectGroupingSelection] = useState<
    SidebarProjectGroupingMode | "inherit"
  >("inherit");
  const renamingCommittedRef = useRef(false);
  const renamingInputRef = useRef<HTMLInputElement | null>(null);
  const memberProjectByScopedKey = useMemo(
    () =>
      new Map(
        threadMembers.map((member) => [
          scopedProjectKey(scopeProjectRef(member.environmentId, member.id)),
          member,
        ]),
      ),
    [threadMembers],
  );
  const memberThreadCountByPhysicalKey = useMemo(() => {
    const counts = new Map<string, number>(
      threadMembers.map((member) => [member.physicalProjectKey, 0] as const),
    );
    for (const thread of projectThreads) {
      const member = memberProjectByScopedKey.get(
        scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
      );
      if (!member) {
        continue;
      }
      counts.set(member.physicalProjectKey, (counts.get(member.physicalProjectKey) ?? 0) + 1);
    }
    return counts;
  }, [memberProjectByScopedKey, threadMembers, projectThreads]);

  const { projectStatus, visibleProjectThreads } = useMemo(() => {
    const lastVisitedAtByThreadKey = new Map(
      projectThreads.map((thread, index) => [
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
        threadLastVisitedAts[index] ?? null,
      ]),
    );
    const resolveProjectThreadStatus = (thread: SidebarThreadSummary) => {
      const lastVisitedAt = lastVisitedAtByThreadKey.get(
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      );
      return resolveThreadStatusPill({
        suppressPlanReady: hasProposedPlanHandoffForThread(
          { bySourceThreadKey: proposedPlanHandoffsBySourceThreadKey },
          scopeThreadRef(thread.environmentId, thread.id),
        ),
        thread: {
          ...thread,
          ...(lastVisitedAt !== null && lastVisitedAt !== undefined ? { lastVisitedAt } : {}),
        },
      });
    };
    const visibleProjectThreads = sortThreads(
      projectThreads.filter((thread) => thread.archivedAt === null),
      threadSortOrder,
    );
    const projectStatus = resolveProjectStatusIndicator(
      visibleProjectThreads.map((thread) => resolveProjectThreadStatus(thread)),
    );
    return {
      orderedProjectThreadKeys: visibleProjectThreads.map((thread) =>
        scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
      ),
      projectStatus,
      visibleProjectThreads,
    };
  }, [
    projectThreads,
    proposedPlanHandoffsBySourceThreadKey,
    threadLastVisitedAts,
    threadSortOrder,
  ]);
  const pinnedCollapsedThread = useMemo(() => {
    const activeThreadKey = activeRouteThreadKey ?? undefined;
    if (!activeThreadKey || projectExpanded) {
      return null;
    }
    return (
      visibleProjectThreads.find(
        (thread) =>
          scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)) === activeThreadKey,
      ) ?? null
    );
  }, [activeRouteThreadKey, projectExpanded, visibleProjectThreads]);

  // The list takes the scope's whole non-archived thread set and does its own
  // sectioning and tail paging; the tree's page window would have truncated
  // the shelves to whatever page happened to be loaded.
  const renderedThreads = visibleProjectThreads;
  const shouldShowThreadPanel = projectExpanded || pinnedCollapsedThread !== null;

  const handleProjectButtonClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (suppressProjectClickForContextMenuRef.current) {
        suppressProjectClickForContextMenuRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (dragInProgressRef.current) {
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (suppressProjectClickAfterDragRef.current) {
        suppressProjectClickAfterDragRef.current = false;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (useThreadSelectionStore.getState().hasSelection()) {
        clearSelection();
      }
      if (allProjectEnvironmentsUnavailable && scheduleProjectMember) {
        if (isMobile) setOpenMobile(false);

        return;
      }
      setProjectExpanded(projectPreferenceKeys, !projectExpanded);
    },
    [
      allProjectEnvironmentsUnavailable,
      clearSelection,
      dragInProgressRef,
      isMobile,
      projectExpanded,
      projectPreferenceKeys,
      scheduleProjectMember,
      setOpenMobile,
      setProjectExpanded,
      suppressProjectClickAfterDragRef,
      suppressProjectClickForContextMenuRef,
    ],
  );

  const handleProjectButtonKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLButtonElement>) => {
      if (event.key !== "Enter" && event.key !== " ") return;
      event.preventDefault();
      if (dragInProgressRef.current) {
        return;
      }
      if (allProjectEnvironmentsUnavailable && scheduleProjectMember) {
        if (isMobile) setOpenMobile(false);

        return;
      }
      setProjectExpanded(projectPreferenceKeys, !projectExpanded);
    },
    [
      allProjectEnvironmentsUnavailable,
      dragInProgressRef,
      isMobile,
      projectExpanded,
      projectPreferenceKeys,
      scheduleProjectMember,
      setOpenMobile,
      setProjectExpanded,
    ],
  );

  const handleProjectButtonPointerDownCapture = useCallback(
    (event: React.PointerEvent<HTMLButtonElement>) => {
      suppressProjectClickForContextMenuRef.current = false;
      if (
        isContextMenuPointerDown({
          button: event.button,
          ctrlKey: event.ctrlKey,
          isMac: isMacPlatform(navigator.platform),
        })
      ) {
        event.stopPropagation();
      }

      suppressProjectClickAfterDragRef.current = false;
    },
    [suppressProjectClickAfterDragRef, suppressProjectClickForContextMenuRef],
  );

  const openProjectRenameDialog = useCallback(
    (member: SidebarProjectGroupMember) => {
      if (unavailableEnvironmentIds.has(member.environmentId)) {
        return;
      }
      setProjectRenameTarget(member);
      setProjectRenameTitle(member.title);
    },
    [unavailableEnvironmentIds],
  );

  const openProjectGroupingDialog = useCallback(
    (member: SidebarProjectGroupMember) => {
      const overrideKey = deriveProjectGroupingOverrideKey(member);
      setProjectGroupingTarget(member);
      setProjectGroupingSelection(
        projectGroupingSettings.sidebarProjectGroupingOverrides?.[overrideKey] ?? "inherit",
      );
    },
    [projectGroupingSettings.sidebarProjectGroupingOverrides],
  );

  const removeProject = useCallback(
    async (member: SidebarProjectGroupMember, options: { force?: boolean } = {}) => {
      const memberProjectRef = scopeProjectRef(member.environmentId, member.id);
      const result = await deleteProject({
        environmentId: member.environmentId,
        input: {
          projectId: member.id,
          expectedRevision: member.updatedAt,
          ...(options.force === true ? { force: true } : {}),
        },
      });
      if (result._tag === "Failure") {
        return result;
      }
      const draftStore = useComposerDraftStore.getState();
      const projectDraftThread = draftStore.getDraftThreadByProjectRef(memberProjectRef);
      if (projectDraftThread) {
        draftStore.clearDraftThread(projectDraftThread.draftId);
      }
      draftStore.clearProjectDraftThreadId(memberProjectRef);
      return result;
    },
    [deleteProject],
  );

  const handleRemoveProject = useCallback(
    async (member: SidebarProjectGroupMember) => {
      if (unavailableEnvironmentIds.has(member.environmentId)) {
        return;
      }
      const api = readLocalApi();
      if (!api) {
        return;
      }

      const memberProjectRef = scopeProjectRef(member.environmentId, member.id);
      const memberThreadCount = memberThreadCountByPhysicalKey.get(member.physicalProjectKey) ?? 0;
      if (memberThreadCount > 0) {
        const warningToastId = toastManager.add(
          stackedThreadToast({
            type: "warning",
            title: "Project is not empty",
            description: "Delete all threads in this project before removing it.",
            actionVariant: "destructive",
            actionProps: {
              children: "Delete anyway",
              onClick: () => {
                void (async () => {
                  toastManager.close(warningToastId);
                  await new Promise<void>((resolve) => {
                    window.setTimeout(resolve, 180);
                  });

                  const latestProjectThreads = Array.from(
                    sidebarThreadByKeyRef.current.values(),
                  ).filter(
                    (thread) =>
                      thread.environmentId === memberProjectRef.environmentId &&
                      thread.projectId === memberProjectRef.projectId,
                  );
                  const confirmed = await api.dialogs.confirm(
                    latestProjectThreads.length > 0
                      ? [
                          `Remove project "${member.title}" and delete its ${latestProjectThreads.length} thread${
                            latestProjectThreads.length === 1 ? "" : "s"
                          }?`,
                          `Path: ${member.workspaceRoot}`,
                          ...(member.environmentLabel
                            ? [`Environment: ${member.environmentLabel}`]
                            : []),
                          "This permanently clears conversation history for those threads.",
                          "This removes only this project entry.",
                          "This action cannot be undone.",
                        ].join("\n")
                      : [
                          `Remove project "${member.title}"?`,
                          `Path: ${member.workspaceRoot}`,
                          ...(member.environmentLabel
                            ? [`Environment: ${member.environmentLabel}`]
                            : []),
                          "This removes only this project entry.",
                        ].join("\n"),
                  );
                  if (!confirmed) {
                    return;
                  }

                  const result = await removeProject(member, { force: true });
                  if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
                    const error = squashAtomCommandFailure(result);
                    toastManager.add(
                      stackedThreadToast({
                        type: "error",
                        title: `Failed to remove "${member.title}"`,
                        description: commandFailureMessage(
                          error,
                          "Unknown error removing project.",
                        ),
                      }),
                    );
                  }
                })().catch((error) => {
                  const message = commandFailureMessage(error, "Unknown error removing project.");
                  console.error("Failed to remove project", {
                    projectId: member.id,
                    environmentId: member.environmentId,
                    ...safeErrorLogAttributes(error),
                  });
                  toastManager.add(
                    stackedThreadToast({
                      type: "error",
                      title: `Failed to remove "${member.title}"`,
                      description: message,
                    }),
                  );
                });
              },
            },
          }),
        );
        return;
      }

      const message = [
        `Remove project "${member.title}"?`,
        `Path: ${member.workspaceRoot}`,
        ...(member.environmentLabel ? [`Environment: ${member.environmentLabel}`] : []),
        "This removes only this project entry.",
      ].join("\n");
      const confirmed = await api.dialogs.confirm(message);
      if (!confirmed) {
        return;
      }

      const result = await removeProject(member);
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        const message = commandFailureMessage(error, "Unknown error removing project.");
        console.error("Failed to remove project", {
          projectId: member.id,
          environmentId: member.environmentId,
          ...safeErrorLogAttributes(error),
        });
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: `Failed to remove "${member.title}"`,
            description: message,
          }),
        );
      }
    },
    [memberThreadCountByPhysicalKey, unavailableEnvironmentIds, removeProject],
  );

  const handleProjectButtonContextMenu = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      suppressProjectClickForContextMenuRef.current = event.type === "contextmenu";
      void (async () => {
        const api = readLocalApi();
        if (!api) return;

        const actionHandlers = new Map<string, () => Promise<void> | void>();
        actionHandlers.set("toggle-expanded", () => {
          suppressProjectClickForContextMenuRef.current = false;
          setProjectExpanded(projectPreferenceKeys, !projectExpanded);
        });
        const makeLeaf = (
          action: "rename" | "grouping" | "copy-path" | "workflows" | "experiments" | "delete",
          member: SidebarProjectGroupMember,
          options?: {
            destructive?: boolean;
            disabled?: boolean;
          },
        ): ContextMenuItem<string> => {
          const id = `${action}:${member.physicalProjectKey}`;
          actionHandlers.set(id, () => {
            switch (action) {
              case "rename":
                openProjectRenameDialog(member);
                return;
              case "grouping":
                openProjectGroupingDialog(member);
                return;
              case "copy-path":
                copyPathToClipboard(member.workspaceRoot, { path: member.workspaceRoot });
                return;
              case "workflows":
                void router.navigate({
                  to: "/workflows",
                  search: {
                    project: scopedProjectKey(scopeProjectRef(member.environmentId, member.id)),
                  },
                });
                return;
              case "experiments":
                void router.navigate({
                  to: "/experiments",
                  search: {
                    project: scopedProjectKey(scopeProjectRef(member.environmentId, member.id)),
                  },
                });
                return;
              case "delete":
                return handleRemoveProject(member);
            }
          });

          return {
            id,
            label: formatProjectMemberActionLabel(member, project.groupedProjectCount),
            ...(options?.destructive ? { destructive: true } : {}),
            ...(options?.disabled ? { disabled: true } : {}),
          };
        };

        const buildTargetedItem = (
          action: "rename" | "grouping" | "copy-path" | "workflows" | "experiments" | "delete",
          label: string,
          options?: {
            destructive?: boolean;
            isDisabled?: (member: SidebarProjectGroupMember) => boolean;
          },
        ): ContextMenuItem<string> => {
          if (threadMembers.length === 1) {
            const singleMember = threadMembers[0]!;
            return {
              ...makeLeaf(action, singleMember, {
                ...(options?.destructive ? { destructive: true } : {}),
                ...(options?.isDisabled?.(singleMember) ? { disabled: true } : {}),
              }),
              label,
              ...(action === "delete" ? { icon: "trash" } : {}),
            };
          }

          return {
            id: `${action}:submenu`,
            label,
            ...(action === "delete" ? { icon: "trash" } : {}),
            children: threadMembers.map((member) =>
              makeLeaf(action, member, {
                ...(options?.destructive ? { destructive: true } : {}),
                ...(options?.isDisabled?.(member) ? { disabled: true } : {}),
              }),
            ),
          };
        };

        const clicked = await api.contextMenu.show(
          [
            {
              id: "toggle-expanded",
              label: projectExpanded ? "Collapse project" : "Expand project",
            },
            buildTargetedItem("rename", "Rename"),
            buildTargetedItem("grouping", "Group into..."),
            buildTargetedItem("copy-path", "Copy Path"),
            buildTargetedItem("workflows", "Workflows"),
            buildTargetedItem("experiments", "Experiments"),
            buildTargetedItem("delete", "Remove", {
              destructive: true,
            }),
          ],
          {
            x: event.clientX,
            y: event.clientY,
          },
        );

        if (!clicked) {
          return;
        }

        await actionHandlers.get(clicked)?.();
      })();
    },
    [
      copyPathToClipboard,
      handleRemoveProject,
      openProjectGroupingDialog,
      openProjectRenameDialog,
      project.groupedProjectCount,
      threadMembers,
      projectExpanded,
      projectPreferenceKeys,
      setProjectExpanded,
      suppressProjectClickForContextMenuRef,
    ],
  );

  const navigateToThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      const targetThreadKey = scopedThreadKey(threadRef);
      if (useThreadSelectionStore.getState().selectedThreadKeys.size > 0) {
        clearSelection();
      }
      setSelectionAnchor(targetThreadKey);
      if (isMobile) {
        setOpenMobile(false);
      }
      void router.navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
    },
    [
      activeRouteThreadKey,
      clearSelection,
      isMobile,
      unavailableEnvironmentIds,
      router,
      setOpenMobile,
      setSelectionAnchor,
    ],
  );

  const handleThreadClick = useCallback(
    (
      event: React.MouseEvent,
      threadRef: ScopedThreadRef,
      orderedProjectThreadKeys: readonly string[],
    ) => {
      const isMac = isMacPlatform(navigator.platform);
      const isModClick = isMac ? event.metaKey : event.ctrlKey;
      const isShiftClick = event.shiftKey;
      const threadKey = scopedThreadKey(threadRef);
      const currentSelectionCount = useThreadSelectionStore.getState().selectedThreadKeys.size;

      if (isModClick) {
        event.preventDefault();
        toggleThreadSelection(threadKey);
        return;
      }

      if (isShiftClick) {
        event.preventDefault();
        rangeSelectTo(threadKey, orderedProjectThreadKeys);
        return;
      }

      // Ignore the trailing click of a plain double-click so it doesn't navigate
      // while a double-click is starting an inline rename. Placed after the
      // modifier branches so cmd/shift selection still processes every click.
      if (isTrailingDoubleClick(event.detail)) {
        return;
      }

      if (currentSelectionCount > 0) {
        clearSelection();
      }
      setSelectionAnchor(threadKey);
      if (isMobile) {
        setOpenMobile(false);
      }
      void router.navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
    },
    [
      clearSelection,
      isMobile,
      rangeSelectTo,
      router,
      setOpenMobile,
      setSelectionAnchor,
      toggleThreadSelection,
    ],
  );

  const handleMultiSelectContextMenu = useCallback(
    async (position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const threadKeys = [...useThreadSelectionStore.getState().selectedThreadKeys];
      if (threadKeys.length === 0) return;
      const count = threadKeys.length;

      const clicked = await api.contextMenu.show(
        [
          { id: "mark-unread", label: `Mark unread (${count})` },
          { id: "delete", label: `Delete (${count})`, destructive: true },
        ],
        position,
      );

      if (clicked === "mark-unread") {
        for (const threadKey of threadKeys) {
          const thread = sidebarThreadByKeyRef.current.get(threadKey);
          markThreadUnread(threadKey, thread?.latestTurn?.completedAt);
        }
        clearSelection();
        return;
      }

      if (clicked !== "delete") return;

      if (appSettingsConfirmThreadDelete) {
        const confirmed = await api.dialogs.confirm(
          [
            `Delete ${count} thread${count === 1 ? "" : "s"}?`,
            "This permanently clears conversation history for these threads.",
          ].join("\n"),
        );
        if (!confirmed) return;
      }

      const deletedThreadKeys = new Set(threadKeys);
      for (const threadKey of threadKeys) {
        const thread = sidebarThreadByKeyRef.current.get(threadKey);
        if (!thread) continue;
        const result = await deleteThread(scopeThreadRef(thread.environmentId, thread.id), {
          deletedThreadKeys,
        });
        if (result._tag === "Failure") {
          if (!isAtomCommandInterrupted(result)) {
            const error = squashAtomCommandFailure(result);
            toastManager.add(
              stackedThreadToast({
                type: "error",
                title: "Failed to delete threads",
                description: commandFailureMessage(error, "An error occurred."),
              }),
            );
          }
          return;
        }
      }
      removeFromSelection(threadKeys);
    },
    [
      appSettingsConfirmThreadDelete,
      clearSelection,
      deleteThread,
      markThreadUnread,
      removeFromSelection,
    ],
  );

  const createThreadForProjectMember = useCreateSidebarThread(
    handleNewThread,
    serverConfigs,
    unavailableEnvironmentIds,
  );

  const handleCreateThreadClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const targetMember = resolveSidebarNewThreadProjectMember(
        threadMembers,
        unavailableEnvironmentIds,
      );
      if (targetMember) {
        createThreadForProjectMember(targetMember);
      }
    },
    [createThreadForProjectMember, threadMembers, unavailableEnvironmentIds],
  );

  const attemptArchiveThread = useCallback(
    async (threadRef: ScopedThreadRef) => {
      const result = await archiveThread(threadRef);
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to archive thread",
            description: commandFailureMessage(error, "An error occurred."),
          }),
        );
      }
    },
    [archiveThread],
  );

  const cancelRename = useCallback(() => {
    setRenamingThreadKey(null);
    renamingInputRef.current = null;
  }, []);

  const startThreadRename = useCallback((threadKey: string, title: string) => {
    setRenamingThreadKey(threadKey);
    setRenamingTitle(title);
    renamingCommittedRef.current = false;
  }, []);

  const commitRename = useCallback(
    async (threadRef: ScopedThreadRef, newTitle: string, originalTitle: string) => {
      const threadKey = scopedThreadKey(threadRef);
      const finishRename = () => {
        setRenamingThreadKey((current) => {
          if (current !== threadKey) return current;
          renamingInputRef.current = null;
          return null;
        });
      };

      const trimmed = newTitle.trim();
      if (trimmed.length === 0) {
        toastManager.add({
          type: "warning",
          title: "Thread title cannot be empty",
        });
        finishRename();
        return;
      }
      if (trimmed === originalTitle) {
        finishRename();
        return;
      }
      const thread = readThreadShell(threadRef);
      if (thread === null) {
        finishRename();
        return;
      }
      const result = await updateThreadMetadata({
        environmentId: threadRef.environmentId,
        input: {
          threadId: threadRef.threadId,
          expectedRevision: thread.updatedAt,
          title: trimmed,
        },
      });
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to rename thread",
            description: commandFailureMessage(error, "An error occurred."),
          }),
        );
      }
      finishRename();
    },
    [updateThreadMetadata],
  );

  const closeProjectRenameDialog = useCallback(() => {
    setProjectRenameTarget(null);
    setProjectRenameTitle("");
  }, []);

  const submitProjectRename = useCallback(async () => {
    if (!projectRenameTarget) {
      return;
    }
    if (unavailableEnvironmentIds.has(projectRenameTarget.environmentId)) {
      closeProjectRenameDialog();

      return;
    }

    const trimmed = projectRenameTitle.trim();
    if (trimmed.length === 0) {
      toastManager.add({
        type: "warning",
        title: "Project title cannot be empty",
      });
      return;
    }

    if (trimmed === projectRenameTarget.title) {
      closeProjectRenameDialog();
      return;
    }

    const result = await updateProject({
      environmentId: projectRenameTarget.environmentId,
      input: {
        projectId: projectRenameTarget.id,
        expectedRevision: projectRenameTarget.updatedAt,
        title: trimmed,
      },
    });
    if (result._tag === "Success") {
      closeProjectRenameDialog();
    } else if (!isAtomCommandInterrupted(result)) {
      const error = squashAtomCommandFailure(result);
      toastManager.add(
        stackedThreadToast({
          type: "error",
          title: "Failed to rename project",
          description: commandFailureMessage(error, "An error occurred."),
        }),
      );
    }
  }, [
    closeProjectRenameDialog,
    projectRenameTarget,
    projectRenameTitle,
    unavailableEnvironmentIds,
    updateProject,
  ]);

  const closeProjectGroupingDialog = useCallback(() => {
    setProjectGroupingTarget(null);
    setProjectGroupingSelection("inherit");
  }, []);

  const saveProjectGroupingPreference = useCallback(() => {
    if (!projectGroupingTarget) {
      return;
    }

    const overrideKey = deriveProjectGroupingOverrideKey(projectGroupingTarget);
    const nextOverrides = {
      ...projectGroupingSettings.sidebarProjectGroupingOverrides,
    };
    if (projectGroupingSelection === "inherit") {
      delete nextOverrides[overrideKey];
    } else {
      nextOverrides[overrideKey] = projectGroupingSelection;
    }
    updateSettings({
      sidebarProjectGroupingOverrides: nextOverrides,
    });
    closeProjectGroupingDialog();
  }, [
    closeProjectGroupingDialog,
    projectGroupingSelection,
    projectGroupingSettings.sidebarProjectGroupingOverrides,
    projectGroupingTarget,
    updateSettings,
  ]);

  const manageThread = useThreadManagement();
  const handleThreadContextMenu = useCallback(
    async (threadRef: ScopedThreadRef, position: { x: number; y: number }) => {
      const api = readLocalApi();
      if (!api) return;
      const threadKey = scopedThreadKey(threadRef);
      const thread = sidebarThreadByKeyRef.current.get(threadKey) ?? null;
      if (!thread) return;
      const threadProject = memberProjectByScopedKey.get(
        scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
      );
      const threadWorkspacePath =
        thread.worktreePath ?? threadProject?.workspaceRoot ?? project.workspaceRoot ?? null;
      const presets = threadSnoozePresets(new Date());
      const section = threadManagementSection(thread, Date.now());
      const clicked = await api.contextMenu.show(
        [
          {
            id: section === "settled" ? "restore" : "settle",
            label: section === "settled" ? "Un-settle thread" : "Settle thread",
          },
          ...(section === "snoozed"
            ? [{ id: "unsnooze", label: "Wake thread now" }]
            : [
                {
                  id: "snooze",
                  label: "Snooze",
                  children: presets.map((preset) => ({
                    id: `snooze:${preset.id}`,
                    label: `${preset.label} · ${preset.whenLabel}`,
                  })),
                },
              ]),
          { id: "archive", label: "Archive thread" },
          {
            id: "pin",
            label: pinnedThreadRefs.some((candidate) => scopedThreadKey(candidate) === threadKey)
              ? "Unpin thread"
              : "Pin thread",
          },
          { id: "rename", label: "Rename thread" },
          ...(thread.titleRegeneration == null
            ? [{ id: "regenerate-title", label: "Regenerate title" }]
            : []),
          { id: "mark-unread", label: "Mark unread" },
          { id: "copy-path", label: "Copy Path" },
          { id: "copy-thread-id", label: "Copy Thread ID" },
          { id: "delete", label: "Delete", destructive: true, icon: "trash" },
        ],
        position,
      );

      if (clicked === "settle" || clicked === "restore" || clicked === "unsnooze") {
        await manageThread(threadRef, clicked);
        return;
      }
      const preset = presets.find((preset) => clicked === `snooze:${preset.id}`);
      if (preset) {
        await manageThread(threadRef, "snooze", preset.until);
        return;
      }
      if (clicked === "archive") {
        if (appSettingsConfirmThreadArchive) {
          const confirmation = await settlePromise(() =>
            api.dialogs.confirm(`Archive thread "${thread.title}"?`),
          );
          if (confirmation._tag === "Failure" || !confirmation.value) return;
        }
        await attemptArchiveThread(threadRef);
        return;
      }
      if (clicked === "pin") {
        if (pinnedThreadRefs.some((candidate) => scopedThreadKey(candidate) === threadKey)) {
          unpinThread(threadRef);
        } else {
          pinThread(threadRef);
        }
        return;
      }

      if (clicked === "rename") {
        startThreadRename(threadKey, thread.title);
        return;
      }

      if (clicked === "regenerate-title") {
        // A regeneration already in flight owns the thread's title; asking again
        // would only supersede it with an identical request.
        if (thread.titleRegeneration != null) return;
        const result = await updateThreadMetadata({
          environmentId: threadRef.environmentId,
          input: {
            threadId: threadRef.threadId,
            expectedRevision: thread.updatedAt,
            regenerateTitle: true,
          },
        });
        if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
          const error = squashAtomCommandFailure(result);
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Failed to regenerate thread title",
              description: commandFailureMessage(error, "An error occurred."),
            }),
          );
        }
        return;
      }

      if (clicked === "mark-unread") {
        markThreadUnread(threadKey, thread.latestTurn?.completedAt);
        return;
      }
      if (clicked === "copy-path") {
        if (!threadWorkspacePath) {
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Path unavailable",
              description: "This thread does not have a workspace path to copy.",
            }),
          );
          return;
        }
        copyPathToClipboard(threadWorkspacePath, { path: threadWorkspacePath });
        return;
      }
      if (clicked === "copy-thread-id") {
        copyThreadIdToClipboard(thread.id, { threadId: thread.id });
        return;
      }
      if (clicked !== "delete") return;
      if (appSettingsConfirmThreadDelete) {
        const confirmed = await api.dialogs.confirm(
          [
            `Delete thread "${thread.title}"?`,
            "This permanently clears conversation history for this thread.",
          ].join("\n"),
        );
        if (!confirmed) {
          return;
        }
      }
      const result = await deleteThread(threadRef);
      if (result._tag === "Failure" && !isAtomCommandInterrupted(result)) {
        const error = squashAtomCommandFailure(result);
        toastManager.add(
          stackedThreadToast({
            type: "error",
            title: "Failed to delete thread",
            description: commandFailureMessage(error, "An error occurred."),
          }),
        );
      }
    },
    [
      appSettingsConfirmThreadArchive,
      manageThread,
      attemptArchiveThread,
      appSettingsConfirmThreadDelete,
      copyPathToClipboard,
      copyThreadIdToClipboard,
      deleteThread,
      markThreadUnread,
      memberProjectByScopedKey,
      pinThread,
      pinnedThreadRefs,
      project.workspaceRoot,
      startThreadRename,
      unpinThread,
    ],
  );

  const openProjectStudy = useProjectStudyDialogStore((store) => store.open);
  // A project created from references awaits its Project Study until one starts or is skipped.
  const projectAwaitingStudy =
    project.memberProjects.find((member) => projectAwaitsStudy(member)) ?? null;
  const projectHeader = (
    <div className="group/project-header relative min-w-0 flex-1">
      <SidebarMenuButton
        ref={isManualProjectSorting ? dragHandleProps?.setActivatorNodeRef : undefined}
        size="sm"
        className={`gap-1.5 px-2 py-1.5 pr-7 text-left hover:bg-accent ${isManualProjectSorting ? "cursor-grab active:cursor-grabbing" : "cursor-pointer"}`}
        {...(isManualProjectSorting && dragHandleProps ? dragHandleProps.attributes : {})}
        {...(isManualProjectSorting && dragHandleProps ? dragHandleProps.listeners : {})}
        onPointerDownCapture={handleProjectButtonPointerDownCapture}
        onClick={handleProjectButtonClick}
        onKeyDown={handleProjectButtonKeyDown}
        onContextMenu={handleProjectButtonContextMenu}
        aria-disabled={allProjectEnvironmentsUnavailable}
        title={`${project.displayName} · ${project.connectionLabel}`}
        aria-expanded={projectExpanded}
      >
        {!projectExpanded && projectStatus ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <span
                  aria-label={projectStatus.label}
                  className={`-ml-0.5 relative inline-flex size-3.5 shrink-0 items-center justify-center ${projectStatus.colorClass}`}
                />
              }
            >
              <span className="absolute inset-0 flex items-center justify-center transition-opacity duration-150 group-hover/project-header:opacity-0">
                <span className={`size-[9px] rounded-full ${projectStatus.dotClass}`} />
              </span>
              <ChevronRightIcon className="absolute inset-0 m-auto size-3.5 text-muted-foreground/70 opacity-0 transition-opacity duration-150 group-hover/project-header:opacity-100" />
            </TooltipTrigger>
            <TooltipPopup side="top">{projectStatus.label}</TooltipPopup>
          </Tooltip>
        ) : (
          <ChevronRightIcon
            className={`absolute left-2 size-3.5 text-muted-foreground/70 opacity-0 transition-[opacity,transform] duration-150 group-hover/project-header:opacity-100 group-focus-within/project-header:opacity-100 ${
              projectExpanded ? "rotate-90" : ""
            }`}
          />
        )}
        <span className="inline-flex shrink-0 group-hover/project-header:opacity-0 group-focus-within/project-header:opacity-0">
          <ProjectFavicon environmentId={project.environmentId} cwd={project.workspaceRoot} />
        </span>
        <span className="flex min-w-0 flex-1 items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-xs font-normal text-muted-foreground">
            {project.displayName}
          </span>
          <span className="sr-only">{project.connectionLabel}</span>
          {project.groupedProjectCount > 1 ? (
            <span className="shrink-0 text-[10px] text-muted-foreground/60">
              {project.groupedProjectCount} projects
            </span>
          ) : null}
          {allProjectEnvironmentsUnavailable ? (
            <span
              className="shrink-0 text-[9px] font-medium text-warning-foreground"
              data-environment-unavailable
            >
              Disconnected
            </span>
          ) : projectAwaitingStudy !== null ? (
            <span className="shrink-0 text-[9px] font-medium text-muted-foreground group-hover/project-header:opacity-0">
              Awaiting study
            </span>
          ) : null}
        </span>
      </SidebarMenuButton>
      <div className="pointer-events-none absolute top-[calc(50%+1px)] right-0.5 flex -translate-y-1/2 items-center opacity-0 transition-opacity duration-150 max-sm:pointer-events-auto max-sm:opacity-100 group-hover/project-header:pointer-events-auto group-hover/project-header:opacity-100 group-focus-within/project-header:pointer-events-auto group-focus-within/project-header:opacity-100">
        {projectAwaitingStudy !== null ? (
          <Tooltip>
            <TooltipTrigger
              render={
                <button
                  type="button"
                  aria-label={`Start the Project Study of ${project.displayName}`}
                  className={SIDEBAR_ICON_ACTION_BUTTON_CLASS}
                  onClick={() =>
                    openProjectStudy(
                      scopeProjectRef(projectAwaitingStudy.environmentId, projectAwaitingStudy.id),
                    )
                  }
                >
                  <ListChecksIcon className="size-3.5" />
                </button>
              }
            />
            <TooltipPopup side="top">Start the study</TooltipPopup>
          </Tooltip>
        ) : null}
        <Tooltip>
          <TooltipTrigger
            render={
              <button
                type="button"
                aria-label={
                  allProjectEnvironmentsUnavailable
                    ? `Environment disconnected for ${project.displayName}`
                    : `Create new thread in ${project.displayName}`
                }
                aria-disabled={allProjectEnvironmentsUnavailable || projectAwaitingStudy !== null}
                data-testid="new-thread-button"
                className={cn(
                  SIDEBAR_ICON_ACTION_BUTTON_CLASS,
                  projectAwaitingStudy !== null && "opacity-50",
                )}
                onClick={(event) => {
                  // The project keeps to its ingest thread until the study starts or is skipped.
                  if (projectAwaitingStudy !== null) {
                    event.preventDefault();
                    event.stopPropagation();
                    return;
                  }
                  handleCreateThreadClick(event);
                }}
              >
                {allProjectEnvironmentsUnavailable ? (
                  <SettingsIcon className="size-3.5" />
                ) : (
                  <SquarePenIcon className="size-3.5" />
                )}
              </button>
            }
          />
          <TooltipPopup side="top">
            {allProjectEnvironmentsUnavailable
              ? "Environment disconnected"
              : projectAwaitingStudy !== null
                ? PROJECT_STUDY_NEW_THREAD_REASON
                : newThreadShortcutLabel
                  ? `New thread (${newThreadShortcutLabel})`
                  : "New thread"}
          </TooltipPopup>
        </Tooltip>
      </div>
    </div>
  );

  return (
    <>
      {props.scopeProjectSnapshots === undefined &&
      (!shouldShowThreadPanel || renderedThreads.length === 0)
        ? projectHeader
        : null}

      {/* One flat list for the scope: pinned, active, the snoozed shelf and
          the settled tail, with the markers between them as drop targets. */}
      <SidebarThreadSections
        threads={renderedThreads}
        activeRouteThreadKey={activeRouteThreadKey}
        manageThread={manageThread}
        renderRow={({ thread, pinned, isActive, orderedThreadKeys, dropVerb }) => (
          <SidebarThreadRow
            manageThread={manageThread}
            pinned={pinned}
            togglePin={togglePin}
            thread={thread}
            projectCwd={project.workspaceRoot}
            orderedProjectThreadKeys={orderedThreadKeys}
            isActive={isActive}
            dropVerb={dropVerb}
            jumpLabel={
              threadJumpLabelByKey.get(
                scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)),
              ) ?? null
            }
            renamingThreadKey={renamingThreadKey}
            renamingTitle={renamingTitle}
            setRenamingTitle={setRenamingTitle}
            startThreadRename={startThreadRename}
            renamingInputRef={renamingInputRef}
            renamingCommittedRef={renamingCommittedRef}
            handleThreadClick={handleThreadClick}
            navigateToThread={navigateToThread}
            handleMultiSelectContextMenu={handleMultiSelectContextMenu}
            handleThreadContextMenu={handleThreadContextMenu}
            clearSelection={clearSelection}
            commitRename={commitRename}
            cancelRename={cancelRename}
            openPrLink={openPrLink}
          />
        )}
      />

      <Dialog
        open={projectRenameTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeProjectRenameDialog();
          }
        }}
      >
        <DialogPopup className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Rename project</DialogTitle>
            <DialogDescription>
              {projectRenameTarget
                ? `Update the title for ${projectRenameTarget.workspaceRoot}.`
                : "Update the project title."}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-foreground">Project title</span>
              <Input
                aria-label="Project title"
                value={projectRenameTitle}
                onChange={(event) => setProjectRenameTitle(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void submitProjectRename();
                  }
                }}
              />
            </div>
            {projectRenameTarget?.environmentLabel ? (
              <p className="text-xs text-muted-foreground">
                Environment: {projectRenameTarget.environmentLabel}
              </p>
            ) : null}
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={closeProjectRenameDialog}>
              Cancel
            </Button>
            <Button onClick={() => void submitProjectRename()}>Save</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>

      <Dialog
        open={projectGroupingTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            closeProjectGroupingDialog();
          }
        }}
      >
        <DialogPopup className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Project grouping</DialogTitle>
            <DialogDescription>
              {projectGroupingTarget
                ? `Choose how ${projectGroupingTarget.workspaceRoot} should be grouped in the sidebar.`
                : "Choose how this project should be grouped in the sidebar."}
            </DialogDescription>
          </DialogHeader>
          <DialogPanel className="space-y-4">
            <div className="grid gap-1.5">
              <span className="text-xs font-medium text-foreground">Grouping rule</span>
              <Select
                value={projectGroupingSelection}
                onValueChange={(value) => {
                  if (
                    value === "inherit" ||
                    value === "repository" ||
                    value === "repository_path" ||
                    value === "separate"
                  ) {
                    setProjectGroupingSelection(value);
                  }
                }}
              >
                <SelectTrigger className="w-full" aria-label="Project grouping rule">
                  <SelectValue>
                    {projectGroupingSelection === "inherit"
                      ? `Use global default (${PROJECT_GROUPING_MODE_LABELS[projectGroupingSettings.sidebarProjectGroupingMode]})`
                      : PROJECT_GROUPING_MODE_LABELS[projectGroupingSelection]}
                  </SelectValue>
                </SelectTrigger>
                <SelectPopup align="end" alignItemWithTrigger={false}>
                  <SelectItem hideIndicator value="inherit">
                    Use global default
                  </SelectItem>
                  <SelectItem hideIndicator value="repository">
                    {PROJECT_GROUPING_MODE_LABELS.repository}
                  </SelectItem>
                  <SelectItem hideIndicator value="repository_path">
                    {PROJECT_GROUPING_MODE_LABELS.repository_path}
                  </SelectItem>
                  <SelectItem hideIndicator value="separate">
                    {PROJECT_GROUPING_MODE_LABELS.separate}
                  </SelectItem>
                </SelectPopup>
              </Select>
            </div>
            <p className="text-xs text-muted-foreground">
              {projectGroupingSelection === "inherit"
                ? projectGroupingModeDescription(projectGroupingSettings.sidebarProjectGroupingMode)
                : projectGroupingModeDescription(projectGroupingSelection)}
            </p>
          </DialogPanel>
          <DialogFooter>
            <Button variant="outline" onClick={closeProjectGroupingDialog}>
              Cancel
            </Button>
            <Button onClick={saveProjectGroupingPreference}>Save</Button>
          </DialogFooter>
        </DialogPopup>
      </Dialog>
    </>
  );
});

type SortableProjectHandleProps = Pick<
  ReturnType<typeof useSortable>,
  "attributes" | "listeners" | "setActivatorNodeRef"
>;

const SidebarChromeHeader = memo(function SidebarChromeHeader({
  isElectron,
}: {
  isElectron: boolean;
}) {
  return isElectron ? (
    <SidebarHeader className="@container/sidebar-header drag-region h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center px-3 py-0 md:px-0">
      <SidebarTrigger className="md:hidden" />
      <SidebarBrand />
    </SidebarHeader>
  ) : (
    <SidebarHeader className="@container/sidebar-header h-[var(--workspace-topbar-height)] shrink-0 flex-row items-center px-3 py-0 md:px-0">
      <SidebarTrigger className="md:hidden" />
      <SidebarBrand />
    </SidebarHeader>
  );
});

function SidebarBrand() {
  return (
    <Link
      aria-label="Go to threads"
      className="sidebar-brand ml-[var(--workspace-titlebar-content-left)] h-7 w-fit min-w-0 shrink-0 items-center gap-1 overflow-hidden rounded-md text-foreground outline-hidden ring-ring focus-visible:ring-2"
      to="/"
    >
      <span className="truncate text-sm font-semibold tracking-tight text-foreground">AFAR</span>
      <span className="truncate text-sm font-medium tracking-tight text-muted-foreground">
        Research
      </span>
      <span
        className={cn(
          "sidebar-brand-stage shrink-0 items-center whitespace-nowrap rounded-full bg-muted/50 px-1.5 py-0.5 text-[8px] font-medium text-muted-foreground/60",
          APP_BRAND_BADGE.kind === "stage"
            ? "uppercase tracking-[0.18em]"
            : "tabular-nums tracking-[0.04em]",
        )}
      >
        {APP_BRAND_BADGE.text}
      </span>
    </Link>
  );
}

const SidebarChromeFooter = memo(function SidebarChromeFooter({
  shellReady,
}: {
  shellReady: boolean;
}) {
  const navigate = useNavigate();
  const pathname = useLocation({ select: (location) => location.pathname });
  const { isMobile, setOpenMobile } = useSidebar();
  const actions = [
    {
      label: "Settings",
      icon: SettingsIcon,
      to: "/settings",
      active: pathname.startsWith("/settings"),
    },
    {
      label: "Pull Requests",
      icon: GitPullRequestIcon,
      to: "/pull-requests",
      active: pathname === "/pull-requests",
    },
    {
      label: "Host schedule",
      icon: CalendarClockIcon,
      to: "/schedule",
      active: pathname === "/schedule",
    },
    {
      label: "Usage",
      icon: ChartNoAxesColumnIcon,
      to: "/usage",
      active: pathname === "/usage",
    },
  ] as const;
  return (
    <SidebarFooter className="shrink-0 gap-2 px-2 py-2">
      {/* Always above the undo notice and the navigation row, so neither moves it.
          Absent only where this client has no way to send feedback at all. */}
      {canSendFeedback() ? (
        <SidebarMenu>
          <SidebarMenuItem>
            <SidebarMenuButton
              className="h-7 gap-2 px-2 text-muted-foreground hover:text-foreground"
              onClick={() => {
                if (isMobile) setOpenMobile(false);
                openFeedbackDialog();
              }}
            >
              <MessageSquareTextIcon className="size-3.5" aria-hidden="true" />
              Send feedback
            </SidebarMenuButton>
          </SidebarMenuItem>
        </SidebarMenu>
      ) : null}
      {/* Above the navigation row rather than inside it: the notice comes and
          goes, and the row's height must not move with it. */}
      <SidebarThreadUndoNotice />
      <SidebarMenu className="h-8 flex-row items-center gap-2" aria-label="Workspace navigation">
        {pathname.startsWith("/projects/") ? (
          <SidebarMenuItem>
            <SidebarMenuButton
              aria-label="Back"
              className="h-7 gap-2 px-2 text-muted-foreground"
              onClick={() => void navigate({ to: "/" })}
            >
              ← Back
            </SidebarMenuButton>
          </SidebarMenuItem>
        ) : (
          actions.map(({ label, icon: Icon, to, active }) => (
            <SidebarMenuItem key={to}>
              <Tooltip>
                <TooltipTrigger
                  render={
                    <SidebarMenuButton
                      aria-label={label}
                      aria-current={active ? "page" : undefined}
                      disabled={sidebarFooterDestination(to, shellReady) === null}
                      className={cn(
                        "size-7 justify-center rounded-md p-0 text-muted-foreground/70 hover:bg-accent hover:text-foreground",
                        active && "bg-accent text-foreground",
                      )}
                      onClick={() => {
                        if (isMobile) setOpenMobile(false);
                        const destination = sidebarFooterDestination(to, shellReady);
                        if (destination === null) return;
                        void navigate(
                          destination === "/schedule"
                            ? { to: destination, search: { view: "host" } }
                            : { to: destination },
                        );
                      }}
                    >
                      <Icon className="size-3.5" aria-hidden="true" />
                    </SidebarMenuButton>
                  }
                />
                <TooltipPopup side="top">{label}</TooltipPopup>
              </Tooltip>
            </SidebarMenuItem>
          ))
        )}
        <SidebarMenuItem className="ml-auto">
          <Tooltip>
            <TooltipTrigger
              render={
                <SidebarMenuButton
                  aria-label="Reload app"
                  className="size-7 justify-center rounded-md p-0 text-muted-foreground/70 hover:bg-accent hover:text-foreground"
                  onClick={() => window.location.reload()}
                >
                  <RefreshCwIcon className="size-3.5" aria-hidden="true" />
                </SidebarMenuButton>
              }
            />
            <TooltipPopup side="top">Reload app</TooltipPopup>
          </Tooltip>
        </SidebarMenuItem>
      </SidebarMenu>
    </SidebarFooter>
  );
});

interface SidebarProjectsContentProps {
  inboxWindow: ThreadDiscoveryResult<SidebarThreadSummary>;
  actionsReady: boolean;
  projectSortOrder: SidebarProjectSortOrder;
  threadSortOrder: SidebarThreadSortOrder;
  projectGroupingMode: SidebarProjectGroupingMode;
  updateSettings: ReturnType<typeof useUpdateClientSettings>;
  openAddProject: () => void;
  isManualProjectSorting: boolean;
  projectDnDSensors: ReturnType<typeof useSensors>;
  projectCollisionDetection: CollisionDetection;
  handleProjectDragStart: (event: DragStartEvent) => void;
  handleProjectDragEnd: (event: DragEndEvent) => void;
  handleProjectDragCancel: (event: DragCancelEvent) => void;
  handleNewThread: ReturnType<typeof useNewThreadHandler>;
  archiveThread: ReturnType<typeof useThreadActions>["archiveThread"];
  deleteThread: ReturnType<typeof useThreadActions>["deleteThread"];
  sortedProjects: readonly SidebarProjectSnapshot[];
  /** Every thread the header's search can reach, before the scope narrows it. */
  searchableThreads: readonly SidebarThreadSummary[];
  projectKeysWithThreads: ReadonlySet<string>;
  activeRouteProjectKey: string | null;
  routeThreadKey: string | null;
  newThreadShortcutLabel: string | null;
  threadJumpLabelByKey: ReadonlyMap<string, string>;
  dragInProgressRef: React.RefObject<boolean>;
  suppressProjectClickAfterDragRef: React.RefObject<boolean>;
  suppressProjectClickForContextMenuRef: React.RefObject<boolean>;
  attachProjectListAutoAnimateRef: (node: HTMLElement | null) => void;
  visibleProjectCount: number;
  hasSavedProjects: boolean;
  shellReady: boolean;
  navigateToThread: (threadRef: ScopedThreadRef) => void;
}

function useCreateSidebarThread(
  handleNewThread: ReturnType<typeof useNewThreadHandler>,
  serverConfigs: ReturnType<typeof useServerConfigs>,
  unavailableEnvironmentIds: ReadonlySet<string>,
) {
  const router = useRouter();
  const { isMobile, setOpenMobile } = useSidebar();

  return useCallback(
    (member: SidebarProjectGroupMember) => {
      if (unavailableEnvironmentIds.has(member.environmentId)) {
        if (isMobile) setOpenMobile(false);

        return;
      }
      const currentRouteParams =
        router.state.matches[router.state.matches.length - 1]?.params ?? {};
      const currentRouteTarget = resolveThreadRouteTarget(currentRouteParams);
      const currentActiveThread =
        currentRouteTarget?.kind === "server"
          ? readThreadShell(currentRouteTarget.threadRef)
          : null;
      const draftStore = useComposerDraftStore.getState();
      const currentActiveDraftThread =
        currentRouteTarget?.kind === "server"
          ? (draftStore.getDraftThread(currentRouteTarget.threadRef) ?? null)
          : currentRouteTarget?.kind === "draft"
            ? (draftStore.getDraftSession(currentRouteTarget.draftId) ?? null)
            : null;
      const seedContext = resolveSidebarNewThreadSeedContext({
        projectId: member.id,
        defaultEnvMode: resolveSidebarNewThreadEnvMode({
          defaultEnvMode:
            getProjectWorkspacePreference(scopeProjectRef(member.environmentId, member.id)) ??
            serverConfigs.get(member.environmentId)?.settings.defaultThreadEnvMode ??
            DEFAULT_SERVER_SETTINGS.defaultThreadEnvMode,
        }),
        activeThread:
          currentActiveThread && currentActiveThread.projectId === member.id
            ? {
                projectId: currentActiveThread.projectId,
                branch: currentActiveThread.branch,
                worktreePath: currentActiveThread.worktreePath,
              }
            : null,
        activeDraftThread:
          currentActiveDraftThread && currentActiveDraftThread.projectId === member.id
            ? {
                projectId: currentActiveDraftThread.projectId,
                branch: currentActiveDraftThread.branch,
                worktreePath: currentActiveDraftThread.worktreePath,
                envMode: currentActiveDraftThread.envMode,
                startFromOrigin: currentActiveDraftThread.startFromOrigin,
              }
            : null,
      });
      if (isMobile) {
        setOpenMobile(false);
      }
      void (async () => {
        const result = await settlePromise(() =>
          handleNewThread(scopeProjectRef(member.environmentId, member.id), {
            ...(seedContext.branch !== undefined ? { branch: seedContext.branch } : {}),
            ...(seedContext.worktreePath !== undefined
              ? { worktreePath: seedContext.worktreePath }
              : {}),
            envMode: seedContext.envMode,
            ...(seedContext.startFromOrigin !== undefined
              ? { startFromOrigin: seedContext.startFromOrigin }
              : {}),
          }),
        );
        if (result._tag === "Failure") {
          const error = squashAtomCommandFailure(result);
          toastManager.add(
            stackedThreadToast({
              type: "error",
              title: "Could not create thread",
              description: commandFailureMessage(error, "An error occurred."),
            }),
          );
        }
      })();
    },
    [handleNewThread, isMobile, unavailableEnvironmentIds, router, serverConfigs, setOpenMobile],
  );
}

const SidebarProjectsContent = memo(function SidebarProjectsContent(
  props: SidebarProjectsContentProps,
) {
  const {
    actionsReady,
    openAddProject,
    handleNewThread,
    archiveThread,
    deleteThread,
    sortedProjects,
    activeRouteProjectKey,
    routeThreadKey,
    newThreadShortcutLabel,
    threadJumpLabelByKey,
    dragInProgressRef,
    suppressProjectClickAfterDragRef,
    suppressProjectClickForContextMenuRef,
    visibleProjectCount,
    hasSavedProjects,
    shellReady,
    navigateToThread,
  } = props;
  const navigate = useNavigate();
  const { isMobile, setOpenMobile } = useSidebar();
  const serverConfigs = useServerConfigs();
  const unavailableEnvironmentIds = useMemo(
    () =>
      new Set(
        sortedProjects.flatMap((project) =>
          project.memberProjects
            .filter((member) => !serverConfigs.has(member.environmentId))
            .map((member) => member.environmentId),
        ),
      ),
    [sortedProjects, serverConfigs],
  );
  const createThreadForProjectMember = useCreateSidebarThread(
    handleNewThread,
    serverConfigs,
    unavailableEnvironmentIds,
  );

  const projectPresentation = useClientSettings((settings) => settings.projectPresentation);
  const selectedProjectKey = useClientSettings(
    (settings) => settings.sidebarProjectScopeKey ?? null,
  );
  const visibleSelectedProjectKey = resolveVisibleSidebarProjectScopeKey(
    selectedProjectKey,
    sortedProjects,
  );
  const searchFieldRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [activeSearchResultIndex, setActiveSearchResultIndex] = useState(0);
  const isSearching = searchQuery.trim().length > 0;
  const updateProjectScope = useUpdateClientSettings();
  const setSelectedProjectKey = (key: string | null) =>
    updateProjectScope({ sidebarProjectScopeKey: key });
  const scopedProjects = resolveSidebarProjectScope(selectedProjectKey, sortedProjects);
  const scopedRefs = new Set(
    scopedProjects.flatMap((project) => project.memberProjectRefs.map(scopedProjectKey)),
  );
  const inSelectedScope = (thread: SidebarThreadSummary) =>
    scopeAllowsThread({
      hasKnownProjects: sortedProjects.length > 0,
      scopedProjectRefKeys: scopedRefs,
      threadProjectRefKey: scopedProjectKey(
        scopeProjectRef(thread.environmentId, thread.projectId),
      ),
    });
  const pickerProjects = sortedProjects.map((project) => ({
    ...project,
    icon: projectPresentation?.[scopedProjectKey(project.memberProjectRefs[0]!)]?.icon,
  }));
  // Search reads the same scope the list does, so narrowing to a project
  // narrows the results too rather than reaching past the current scope.
  const searchResults: readonly SidebarThreadSummary[] = searchSidebarThreads(
    props.searchableThreads.filter(inSelectedScope),
    searchQuery,
  );
  const searchResultKeys = searchResults
    .map((thread) => scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)))
    .join("\0");
  useEffect(() => {
    setActiveSearchResultIndex(0);
  }, [searchResultKeys]);
  const selectSearchResult = (thread: SidebarThreadSummary) => {
    if (isMobile) setOpenMobile(false);
    setSearchQuery("");
    navigateToThread(scopeThreadRef(thread.environmentId, thread.id));
  };
  const handleSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return;
    if (event.key === "Escape" && isSearching) {
      event.preventDefault();
      setSearchQuery("");
      return;
    }
    if (searchResults.length === 0) return;
    if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      event.preventDefault();
      const step = event.key === "ArrowDown" ? 1 : -1;
      setActiveSearchResultIndex(
        (index) => (index + step + searchResults.length) % searchResults.length,
      );
      return;
    }
    if (event.key === "Enter") {
      const thread = searchResults[activeSearchResultIndex];
      if (thread === undefined) return;
      event.preventDefault();
      selectSearchResult(thread);
    }
  };
  const handleNewThreadClick = () => {
    if (isMobile) setOpenMobile(false);
    const project =
      scopedProjects.find((item) => item.projectKey === activeRouteProjectKey) ?? scopedProjects[0];
    if (!project) {
      void navigate({ to: "/" });
      return;
    }
    const member = resolveSidebarNewThreadProjectMember(
      project.memberProjects,
      unavailableEnvironmentIds,
    );
    if (member) createThreadForProjectMember(member);
  };

  return (
    <SidebarContent className="gap-0">
      {actionsReady ? (
        <SidebarGroup className="px-2 pt-2 pb-1">
          <SidebarThreadHeader
            searchFieldRef={searchFieldRef}
            hasProjects={sortedProjects.length > 0}
            projectScope={
              <SidebarProjectPicker
                anchorRef={searchFieldRef}
                projects={pickerProjects}
                selectedKey={visibleSelectedProjectKey}
                onSelect={setSelectedProjectKey}
                onSettings={(projectKey) => {
                  if (isMobile) setOpenMobile(false);
                  void navigate({ to: "/settings/projects", search: { project: projectKey } });
                }}
              />
            }
            onNewProject={openAddProject}
            onNewThread={handleNewThreadClick}
            newThreadDisabled={visibleProjectCount === 0}
            newThreadShortcutLabel={newThreadShortcutLabel}
            searchInputRef={searchInputRef}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            onSearchKeyDown={handleSearchKeyDown}
            isSearching={isSearching}
            searchResultCount={searchResults.length}
            activeSearchResultIndex={activeSearchResultIndex}
            onClearSearch={() => setSearchQuery("")}
          />
        </SidebarGroup>
      ) : null}
      <SidebarGroup className="px-2 py-2">
        {isSearching ? (
          searchResults.length > 0 ? (
            <ul
              id="sidebar-thread-search-results"
              role="listbox"
              aria-label="Thread search results"
              className="flex flex-col gap-0.5"
            >
              {searchResults.map((thread, index) => {
                const threadKey = scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id));
                return (
                  <li key={threadKey} className="list-none">
                    <button
                      type="button"
                      id={`sidebar-thread-search-result-${index}`}
                      role="option"
                      aria-selected={activeSearchResultIndex === index}
                      data-testid="sidebar-search-result"
                      className={cn(
                        "flex w-full min-w-0 cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-sm",
                        activeSearchResultIndex === index
                          ? "bg-accent text-foreground"
                          : "text-muted-foreground hover:bg-accent hover:text-foreground",
                      )}
                      onPointerMove={() => setActiveSearchResultIndex(index)}
                      onClick={() => selectSearchResult(thread)}
                    >
                      <span className="min-w-0 flex-1 truncate">{thread.title}</span>
                      {thread.branch ? (
                        <span className="shrink-0 truncate text-xs text-muted-foreground/50">
                          {thread.branch}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p role="status" className="px-2 py-6 text-center text-xs text-muted-foreground">
              No threads found
            </p>
          )
        ) : null}

        {!isSearching && scopedProjects[0] && (
          <SidebarMenu>
            <SidebarProjectItem
              project={scopedProjects[0]}
              scopeProjectSnapshots={scopedProjects}
              activeRouteThreadKey={routeThreadKey}
              newThreadShortcutLabel={newThreadShortcutLabel}
              handleNewThread={handleNewThread}
              archiveThread={archiveThread}
              deleteThread={deleteThread}
              threadJumpLabelByKey={threadJumpLabelByKey}
              dragInProgressRef={dragInProgressRef}
              suppressProjectClickAfterDragRef={suppressProjectClickAfterDragRef}
              suppressProjectClickForContextMenuRef={suppressProjectClickForContextMenuRef}
              isManualProjectSorting={false}
              dragHandleProps={null}
            />
          </SidebarMenu>
        )}

        {!isSearching &&
          visibleProjectCount === 0 &&
          (!shellReady ? (
            <SidebarProjectsGhost />
          ) : (
            <div className="flex flex-col items-center gap-2 px-2 pt-4 text-center">
              <p className="text-xs text-muted-foreground/60">
                {hasSavedProjects ? "No connected projects" : "No projects yet"}
              </p>
              {/* A thread belongs to a project, so an empty Host Store cannot start
                  one. Adding a project lives in the command palette, which a first
                  run has no reason to know about; offer it here rather than leaving
                  the sidebar a dead end. */}
              {hasSavedProjects ? (
                <Button
                  type="button"
                  size="xs"
                  variant="outline"
                  onClick={() => void navigate({ to: "/settings/connections" })}
                >
                  Connections
                </Button>
              ) : (
                <Button type="button" size="xs" variant="outline" onClick={openAddProject}>
                  Add project
                </Button>
              )}
            </div>
          ))}
      </SidebarGroup>
    </SidebarContent>
  );
});

export default function Sidebar() {
  const projects = useProjects();
  const sidebarThreads = useThreadShells();
  const threadDiscovery = useThreadDiscovery();
  const proposedPlanHandoffsBySourceThreadKey = useProposedPlanHandoffStore(
    (state) => state.bySourceThreadKey,
  );
  const projectOrder = useUiStateStore((store) => store.projectOrder);
  const reorderProjects = useUiStateStore((store) => store.reorderProjects);
  const navigate = useNavigate();
  const pathname = useLocation({ select: (loc) => loc.pathname });
  const isOnSettings = pathname.startsWith("/settings");
  const sidebarThreadSortOrder = useClientSettings((s) => s.sidebarThreadSortOrder);
  const sidebarProjectSortOrder = useClientSettings((s) => s.sidebarProjectSortOrder);
  const sidebarProjectGroupingMode = useClientSettings((s) => s.sidebarProjectGroupingMode);
  const projectGroupingSettings = useClientSettings(selectProjectGroupingSettings);
  const updateSettings = useUpdateClientSettings();
  const handleNewThread = useNewThreadHandler();
  const serverConfigs = useServerConfigs();

  const { archiveThread, deleteThread } = useThreadActions();
  const manageThread = useThreadManagement();
  const { isMobile, setOpenMobile } = useSidebar();
  const routeThreadRef = useParams({
    strict: false,
    select: (params) => resolveThreadRouteRef(params),
  });
  const routeThreadKey = routeThreadRef ? scopedThreadKey(routeThreadRef) : null;
  const routeTerminalOpen = useTerminalUiStateStore((state) =>
    routeThreadRef
      ? selectThreadTerminalUiState(state.terminalUiStateByThreadKey, routeThreadRef).terminalOpen
      : false,
  );
  const keybindings = useAtomValue(primaryServerKeybindingsAtom);
  const openAddProjectCommandPalette = useOpenAddProjectCommandPalette();
  const { showThreadJumpHints, updateThreadJumpHintsVisibility } = useThreadJumpHintVisibility();
  const dragInProgressRef = useRef(false);
  const suppressProjectClickAfterDragRef = useRef(false);
  const suppressProjectClickForContextMenuRef = useRef(false);
  const clearSelection = useThreadSelectionStore((s) => s.clearSelection);
  const setSelectionAnchor = useThreadSelectionStore((s) => s.setAnchor);
  const platform = navigator.platform;
  const shortcutModifiers = useShortcutModifierState();
  const { environments } = useEnvironments();
  const primaryEnvironmentId = usePrimaryEnvironmentId();
  // Until the shell can open, the sidebar stands in with the project list's shape and
  // offers nothing to act on; the main pane says why.
  const shellReady = useWorkspaceShellEntry().kind === "ready";
  const actionsReady = shellReady && hasReadyProjectEnvironment(environments);
  useEffect(() => {
    useProposedPlanHandoffStore
      .getState()
      .reconcile(
        sidebarThreads
          .filter((thread) => thread.hasActionableProposedPlan)
          .map((thread) => scopeThreadRef(thread.environmentId, thread.id)),
      );
  }, [proposedPlanHandoffsBySourceThreadKey, sidebarThreads]);
  const environmentLabelById = useMemo(
    () =>
      new Map(
        environments.map((environment) => [environment.environmentId, environment.label] as const),
      ),
    [environments],
  );
  const orderedProjects = useMemo(() => {
    return orderItemsByPreferredIds({
      items: projects,
      preferredIds: projectOrder,
      getId: getProjectOrderKey,
      getPreferenceIds: (project) => [getProjectOrderKey(project)],
    });
  }, [projectOrder, projects]);
  const connectedEnvironmentIds = useMemo(() => new Set(serverConfigs.keys()), [serverConfigs]);
  const visibleProjects = useMemo(
    () => connectedSidebarProjects(orderedProjects, connectedEnvironmentIds),
    [connectedEnvironmentIds, orderedProjects],
  );

  // Build a mapping from physical project key → logical project key for
  // cross-environment grouping.  Projects that share a repositoryIdentity
  // canonicalKey are treated as one logical project in the sidebar.
  const physicalToLogicalKey = useMemo(() => {
    return buildPhysicalToLogicalProjectKeyMap({
      projects: visibleProjects,
      settings: projectGroupingSettings,
      primaryEnvironmentId,
    });
  }, [visibleProjects, primaryEnvironmentId, projectGroupingSettings]);
  const projectPhysicalKeyByScopedRef = useMemo(
    () =>
      new Map(
        orderedProjects.map((project) => [
          scopedProjectKey(scopeProjectRef(project.environmentId, project.id)),
          derivePhysicalProjectKey(project),
        ]),
      ),
    [orderedProjects],
  );

  const sidebarProjects = useMemo<SidebarProjectSnapshot[]>(() => {
    return buildSidebarProjectSnapshots({
      projects: visibleProjects,
      settings: projectGroupingSettings,
      resolveEnvironmentLabel: (environmentId) => environmentLabelById.get(environmentId) ?? null,
      primaryEnvironmentId,
    });
  }, [environmentLabelById, visibleProjects, primaryEnvironmentId, projectGroupingSettings]);

  const sidebarProjectByKey = useMemo(
    () => new Map(sidebarProjects.map((project) => [project.projectKey, project] as const)),
    [sidebarProjects],
  );
  const sidebarThreadByKey = useMemo(
    () =>
      new Map(
        sidebarThreads.map(
          (thread) =>
            [scopedThreadKey(scopeThreadRef(thread.environmentId, thread.id)), thread] as const,
        ),
      ),
    [sidebarThreads],
  );
  // Resolve the active route's project key to a logical key so it matches the
  // sidebar's grouped project entries.
  const activeRouteProjectKey = useMemo(() => {
    if (!routeThreadKey) {
      return null;
    }
    const activeThread = sidebarThreadByKey.get(routeThreadKey);
    if (!activeThread) return null;
    const physicalKey =
      projectPhysicalKeyByScopedRef.get(
        scopedProjectKey(scopeProjectRef(activeThread.environmentId, activeThread.projectId)),
      ) ?? scopedProjectKey(scopeProjectRef(activeThread.environmentId, activeThread.projectId));
    return physicalToLogicalKey.get(physicalKey) ?? physicalKey;
  }, [routeThreadKey, sidebarThreadByKey, physicalToLogicalKey, projectPhysicalKeyByScopedRef]);

  const getCurrentSidebarShortcutContext = useCallback(
    () => ({
      terminalFocus: isTerminalFocused(),
      terminalOpen: routeTerminalOpen,
      modelPickerOpen: isModelPickerOpen(),
      editableFocus: isEditableFocused(),
    }),
    [routeTerminalOpen],
  );
  const newThreadShortcutLabelOptions = useMemo(
    () => ({
      platform,
      context: {
        terminalFocus: false,
        terminalOpen: false,
      },
    }),
    [platform],
  );
  const newThreadShortcutLabel =
    shortcutLabelForCommand(keybindings, "chat.newLocal", newThreadShortcutLabelOptions) ??
    shortcutLabelForCommand(keybindings, "chat.new", newThreadShortcutLabelOptions);

  const navigateToThread = useCallback(
    (threadRef: ScopedThreadRef) => {
      const targetThreadKey = scopedThreadKey(threadRef);
      if (useThreadSelectionStore.getState().selectedThreadKeys.size > 0) {
        clearSelection();
      }
      setSelectionAnchor(targetThreadKey);
      if (isMobile) {
        setOpenMobile(false);
      }
      void navigate({
        to: "/$environmentId/$threadId",
        params: buildThreadRouteParams(threadRef),
      });
    },
    [
      clearSelection,
      isMobile,
      navigate,
      routeThreadKey,
      serverConfigs,
      setOpenMobile,
      setSelectionAnchor,
    ],
  );

  const projectDnDSensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 6 },
    }),
  );
  const projectCollisionDetection = useCallback<CollisionDetection>((args) => {
    const pointerCollisions = pointerWithin(args);
    if (pointerCollisions.length > 0) {
      return pointerCollisions;
    }

    return closestCorners(args);
  }, []);

  const handleProjectDragEnd = useCallback(
    (event: DragEndEvent) => {
      if (sidebarProjectSortOrder !== "manual") {
        dragInProgressRef.current = false;
        return;
      }
      dragInProgressRef.current = false;
      const { active, over } = event;
      if (!over || active.id === over.id) return;
      const activeProject = sidebarProjects.find((project) => project.projectKey === active.id);
      const overProject = sidebarProjects.find((project) => project.projectKey === over.id);
      if (!activeProject || !overProject) return;
      const activeMemberKeys = activeProject.memberProjects.map(
        (member) => member.physicalProjectKey,
      );
      const overMemberKeys = overProject.memberProjects.map((member) => member.physicalProjectKey);
      reorderProjects(orderedProjects.map(getProjectOrderKey), activeMemberKeys, overMemberKeys);
    },
    [orderedProjects, sidebarProjectSortOrder, reorderProjects, sidebarProjects],
  );

  const handleProjectDragStart = useCallback(
    (_event: DragStartEvent) => {
      if (sidebarProjectSortOrder !== "manual") {
        return;
      }
      dragInProgressRef.current = true;
      suppressProjectClickAfterDragRef.current = true;
    },
    [sidebarProjectSortOrder],
  );

  const handleProjectDragCancel = useCallback((_event: DragCancelEvent) => {
    dragInProgressRef.current = false;
  }, []);

  const animatedProjectListsRef = useRef(new WeakSet<HTMLElement>());
  const attachProjectListAutoAnimateRef = useCallback((node: HTMLElement | null) => {
    if (!node || animatedProjectListsRef.current.has(node)) {
      return;
    }
    autoAnimate(node, SIDEBAR_LIST_ANIMATION_OPTIONS);
    animatedProjectListsRef.current.add(node);
  }, []);

  const visibleThreads = useMemo(
    () => sidebarThreads.filter((thread) => thread.archivedAt === null),
    [sidebarThreads],
  );
  const sortedProjects = useMemo(() => {
    const sortableProjects = sidebarProjects.map((project) => ({
      ...project,
      id: project.projectKey,
    }));
    const sortableThreads = visibleThreads.map((thread) => {
      const physicalKey =
        projectPhysicalKeyByScopedRef.get(
          scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId)),
        ) ?? scopedProjectKey(scopeProjectRef(thread.environmentId, thread.projectId));
      return {
        ...thread,
        projectId: (physicalToLogicalKey.get(physicalKey) ?? physicalKey) as ProjectId,
      };
    });
    return sortProjectsForSidebar(
      sortableProjects,
      sortableThreads,
      sidebarProjectSortOrder,
    ).flatMap((project) => {
      const resolvedProject = sidebarProjectByKey.get(project.id);
      return resolvedProject ? [resolvedProject] : [];
    });
  }, [
    sidebarProjectSortOrder,
    physicalToLogicalKey,
    projectPhysicalKeyByScopedRef,
    sidebarProjectByKey,
    sidebarProjects,
    visibleThreads,
  ]);
  const projectKeysWithThreads = useMemo(
    () => getSidebarProjectKeysWithThreads(sidebarProjects, visibleThreads),
    [sidebarProjects, visibleThreads],
  );
  const isManualProjectSorting = sidebarProjectSortOrder === "manual";
  const selectedScopeKey = useClientSettings((settings) => settings.sidebarProjectScopeKey ?? null);
  const [inboxOffset, setInboxOffset] = useState(0);
  useEffect(() => setInboxOffset(0), [selectedScopeKey]);
  const [settledExpanded] = useLocalStorage("afar:sidebar:settled-expanded", false, Schema.Boolean);
  const [snoozedExpanded] = useLocalStorage("afar:sidebar:snoozed-expanded", false, Schema.Boolean);
  const inboxProjects = resolveSidebarProjectScope(selectedScopeKey, sortedProjects);
  const inboxWindow = threadDiscovery.discover({
    groups: ["pinned", "live"],
    projectRefs: inboxProjects.flatMap((project) => project.memberProjectRefs),
    offset: inboxOffset,
    limit: THREAD_DISCOVERY_RENDER_LIMIT,
    activeThreadRef: routeThreadRef,
  });
  const visibleSidebarThreadKeys = inboxWindow.items
    .toSorted((left, right) => {
      const ranks = { active: 0, snoozed: 1, settled: 2 };
      return (
        ranks[threadManagementSection(left.thread, Date.now())] -
        ranks[threadManagementSection(right.thread, Date.now())]
      );
    })
    .filter((item) => {
      const section = threadManagementSection(item.thread, Date.now());
      return (
        section === "active" ||
        (section === "snoozed" ? snoozedExpanded : settledExpanded) ||
        item.key === routeThreadKey
      );
    })
    .map((item) => item.key);
  const threadJumpCommandByKey = useMemo(() => {
    const mapping = new Map<string, NonNullable<ReturnType<typeof threadJumpCommandForIndex>>>();
    for (const [visibleThreadIndex, threadKey] of visibleSidebarThreadKeys.entries()) {
      const jumpCommand = threadJumpCommandForIndex(visibleThreadIndex);
      if (!jumpCommand) {
        return mapping;
      }
      mapping.set(threadKey, jumpCommand);
    }

    return mapping;
  }, [visibleSidebarThreadKeys]);
  const threadJumpThreadKeys = useMemo(
    () => [...threadJumpCommandByKey.keys()],
    [threadJumpCommandByKey],
  );
  const sidebarShortcutContext = {
    terminalFocus: false,
    terminalOpen: routeTerminalOpen,
    modelPickerOpen: isModelPickerOpen(),
  };
  const threadJumpLabelByKey = useMemo(
    () =>
      buildThreadJumpLabelMap({
        keybindings,
        platform,
        terminalOpen: sidebarShortcutContext.terminalOpen,
        threadJumpCommandByKey,
      }),
    [keybindings, platform, sidebarShortcutContext.terminalOpen, threadJumpCommandByKey],
  );
  const shouldShowThreadJumpHintsNow = shouldShowThreadJumpHintsForModifiers(
    shortcutModifiers,
    keybindings,
    {
      platform,
      context: sidebarShortcutContext,
    },
  );
  const visibleThreadJumpLabelByKey = showThreadJumpHints
    ? threadJumpLabelByKey
    : EMPTY_THREAD_JUMP_LABELS;
  const orderedSidebarThreadKeys = visibleSidebarThreadKeys;
  const prewarmedSidebarThreadKeys = useMemo(
    () =>
      getSidebarThreadPrewarmPlan(visibleSidebarThreadKeys, routeThreadKey).map(
        ({ threadId }) => threadId,
      ),
    [routeThreadKey, visibleSidebarThreadKeys],
  );
  const prewarmedSidebarThreadRefs = useMemo(
    () =>
      prewarmedSidebarThreadKeys.flatMap((threadKey) => {
        const ref = parseScopedThreadKey(threadKey);
        return ref !== null &&
          supportsBoundedThreadHistoryPrewarm(serverConfigs.get(ref.environmentId)?.environment)
          ? [ref]
          : [];
      }),
    [prewarmedSidebarThreadKeys, serverConfigs],
  );

  useEffect(() => {
    updateThreadJumpHintsVisibility(shouldShowThreadJumpHintsNow);
  }, [shouldShowThreadJumpHintsNow, updateThreadJumpHintsVisibility]);

  useEffect(() => {
    const onWindowKeyDown = (event: globalThis.KeyboardEvent) => {
      const shortcutContext = getCurrentSidebarShortcutContext();

      if (event.defaultPrevented || event.repeat) {
        return;
      }

      const command = resolveShortcutCommand(event, keybindings, {
        platform,
        context: shortcutContext,
      });
      if (command === "thread.undo") {
        // Only claim the chord when there is something to reverse, so mod+z
        // still reaches the browser when no thread action is outstanding.
        if (!undoLatestThreadAction()) return;
        event.preventDefault();
        event.stopPropagation();
        return;
      }
      if (
        command === "thread.settle" ||
        command === "thread.restore" ||
        command === "thread.snooze" ||
        command === "thread.unsnooze"
      ) {
        const target = routeThreadKey ? sidebarThreadByKey.get(routeThreadKey) : undefined;
        if (!target) return;
        event.preventDefault();
        event.stopPropagation();
        const action =
          command === "thread.settle"
            ? "settle"
            : command === "thread.restore"
              ? "restore"
              : command === "thread.snooze"
                ? "snooze"
                : "unsnooze";
        void manageThread(
          scopeThreadRef(target.environmentId, target.id),
          action,
          action === "snooze" ? threadSnoozePresets(new Date())[0]!.until : undefined,
        );
        return;
      }
      const traversalDirection = threadTraversalDirectionFromCommand(command);
      if (traversalDirection !== null) {
        const targetThreadKey = resolveAdjacentThreadId({
          threadIds: orderedSidebarThreadKeys,
          currentThreadId: routeThreadKey,
          direction: traversalDirection,
        });
        if (!targetThreadKey) {
          return;
        }
        const targetThread = sidebarThreadByKey.get(targetThreadKey);
        if (!targetThread) {
          return;
        }

        event.preventDefault();
        event.stopPropagation();
        navigateToThread(scopeThreadRef(targetThread.environmentId, targetThread.id));
        return;
      }

      const jumpIndex = threadJumpIndexFromCommand(command ?? "");
      if (jumpIndex === null) {
        return;
      }

      const targetThreadKey = threadJumpThreadKeys[jumpIndex];
      if (!targetThreadKey) {
        return;
      }
      const targetThread = sidebarThreadByKey.get(targetThreadKey);
      if (!targetThread) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      navigateToThread(scopeThreadRef(targetThread.environmentId, targetThread.id));
    };

    window.addEventListener("keydown", onWindowKeyDown);

    return () => {
      window.removeEventListener("keydown", onWindowKeyDown);
    };
  }, [
    getCurrentSidebarShortcutContext,
    manageThread,
    keybindings,
    navigateToThread,
    orderedSidebarThreadKeys,
    platform,
    routeThreadKey,
    sidebarThreadByKey,
    threadJumpThreadKeys,
  ]);

  useEffect(() => {
    const onMouseDown = (event: globalThis.MouseEvent) => {
      if (!useThreadSelectionStore.getState().hasSelection()) return;
      const target = event.target instanceof HTMLElement ? event.target : null;
      if (!shouldClearThreadSelectionOnMouseDown(target)) return;
      clearSelection();
    };

    window.addEventListener("mousedown", onMouseDown);
    return () => {
      window.removeEventListener("mousedown", onMouseDown);
    };
  }, [clearSelection]);

  return (
    <>
      {prewarmedSidebarThreadRefs.map((threadRef) => (
        <SidebarThreadDetailPrewarmer key={scopedThreadKey(threadRef)} threadRef={threadRef} />
      ))}
      <SidebarChromeHeader isElectron={isElectron} />

      {isOnSettings ? (
        <SettingsSidebarNav pathname={pathname} />
      ) : (
        <>
          <SidebarProjectsContent
            inboxWindow={inboxWindow}
            actionsReady={actionsReady}
            projectSortOrder={sidebarProjectSortOrder}
            threadSortOrder={sidebarThreadSortOrder}
            projectGroupingMode={sidebarProjectGroupingMode}
            updateSettings={updateSettings}
            openAddProject={openAddProjectCommandPalette}
            isManualProjectSorting={isManualProjectSorting}
            projectDnDSensors={projectDnDSensors}
            projectCollisionDetection={projectCollisionDetection}
            handleProjectDragStart={handleProjectDragStart}
            handleProjectDragEnd={handleProjectDragEnd}
            handleProjectDragCancel={handleProjectDragCancel}
            handleNewThread={handleNewThread}
            archiveThread={archiveThread}
            deleteThread={deleteThread}
            sortedProjects={sortedProjects}
            searchableThreads={sidebarThreads.filter((thread) =>
              connectedEnvironmentIds.has(thread.environmentId),
            )}
            projectKeysWithThreads={projectKeysWithThreads}
            activeRouteProjectKey={activeRouteProjectKey}
            routeThreadKey={routeThreadKey}
            newThreadShortcutLabel={newThreadShortcutLabel}
            threadJumpLabelByKey={visibleThreadJumpLabelByKey}
            dragInProgressRef={dragInProgressRef}
            suppressProjectClickAfterDragRef={suppressProjectClickAfterDragRef}
            suppressProjectClickForContextMenuRef={suppressProjectClickForContextMenuRef}
            attachProjectListAutoAnimateRef={attachProjectListAutoAnimateRef}
            visibleProjectCount={sidebarProjects.length}
            hasSavedProjects={projects.length > 0}
            shellReady={shellReady}
            navigateToThread={navigateToThread}
          />

          <SidebarSeparator />
          <SidebarChromeFooter shellReady={shellReady} />
        </>
      )}
    </>
  );
}
